/**
 * Admin Template Detail Page - E9 Template Management System
 * Full-screen layout with left config panel, right preview panel
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router';
import { 
  ArrowLeft, 
  Plus, 
  Save,
  Loader2,
  X,
  Search,
  Home,
  FileQuestion,
  Send,
  Paperclip,
  Copy,
  Check,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  useTemplate, 
  useTemplates,
  useCreateTemplate, 
  useUpdateTemplate,
} from '@/hooks/use-templates';
import { 
  TemplateStatus, 
  TemplateType,
  TEMPLATE_TYPE_LABELS,
  TEMPLATE_STATUS_LABELS,
  TEMPLATE_CATEGORY_LABELS,
} from '@audra-flow/types';
import { useToast } from '@/hooks/use-toast';
import { useAuthToken } from '@/hooks/use-auth';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { TemplateIcon } from '@/components/ui/template-icon';

// Icon categories for template (matching project emoji picker)
const TEMPLATE_ICON_CATEGORIES = {
  'Work & Business': [
    '💼', '📊', '📈', '💻', '🖥️', '📱', '⚙️', '🔧', '🛠️',
    '📋', '📌', '📎', '🔍', '🎯', '💡', '🚀', '📁', '🗂️',
    '📂', '🗃️', '📇', '🗄️', '📑', '🧮', '💹', '📉', '🏢', '🏛️',
  ],
  'Design & Research': [
    '🎨', '🖌️', '🖍️', '✏️', '🖊️', '📐', '📏', '✂️', '🖼️',
    '👤', '👥', '🗺️', '🧭', '🔬', '🧪', '📝', '🗒️', '📓',
    '🎭', '🎬', '📷', '📹', '🎵', '🎶', '🎼', '🎹', '🥁', '🎷',
  ],
  'Communication': [
    '💬', '💭', '🗨️', '📞', '📲', '📧', '✉️', '📩', '📨',
    '📫', '🔔', '📢', '📣', '🗣️', '👂', '🔊', '📡', '📺',
    '📻', '🎙️', '🎧', '📱', '💻', '🌐', '🔗', '📎', '🏷️', '🔖',
  ],
  'Technology': [
    '🤖', '🎧', '📡', '🔌', '💾', '📀', '🖨️', '⌨️', '🖱️',
    '🔋', '📸', '🎥', '📺', '☁️', '🔐', '🛡️', '🔰', '⚡',
    '💻', '🖥️', '📱', '🎮', '🕹️', '🔦', '🔭', '🔬', '🧲', '💿',
  ],
  'Education': [
    '📚', '🎓', '✏️', '📝', '🎨', '🎭', '🎬', '🎮', '🎵',
    '🌍', '🔭', '🧪', '📐', '📰', '🖋️', '🎹', '🎸', '📖',
    '📕', '📗', '📘', '📙', '🔖', '🏫', '🧑‍🎓', '👨‍🏫', '👩‍🏫', '🎯',
  ],
  'Nature & Science': [
    '🌱', '🌿', '🌲', '🌳', '🌴', '🌵', '🌻', '🌺', '🌸',
    '🌈', '☀️', '🌙', '⭐', '🌊', '🔥', '💧', '❄️', '🌪️',
    '🧬', '🔬', '🔭', '🧪', '🧫', '🧲', '⚗️', '🌡️', '🌍', '🌎',
  ],
};

// AI Model options
const MODEL_OPTIONS = [
  { value: 'deepseek-chat', label: 'DeepSeek Chat' },
  { value: 'deepseek-reasoner', label: 'DeepSeek Reasoner' },
];

// Status badge
function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    ACTIVE: 'bg-green-100 text-green-700',
    DRAFT: 'bg-gray-100 text-gray-700',
    IN_REVIEW: 'bg-blue-100 text-blue-700',
    DEPRECATED: 'bg-orange-100 text-orange-700',
  };
  const label = TEMPLATE_STATUS_LABELS[status as TemplateStatus] || status;
  
  return (
    <span className={cn('px-2.5 py-1 rounded-md text-sm font-medium', colors[status] || colors.DRAFT)}>
      {label}
    </span>
  );
}

// Required field indicator
function RequiredLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-gray-900 font-medium mb-2">
      {children} <span className="text-red-500">*</span>
    </label>
  );
}

// Convert CamelCase or snake_case to readable title with spaces
function formatCamelCaseTitle(text: string): string {
  return text
    .replace(/Artifact$/i, '') // Remove trailing "Artifact"
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before capitals following lowercase
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // Handle consecutive capitals
    .replace(/\s+/g, ' ') // Normalize multiple spaces
    .trim();
}

// Parse markdown into sections for styled rendering with colors
function parseMarkdownSections(content: string): Array<{ title: string; content: string }> {
  const sections: Array<{ title: string; content: string }> = [];
  const lines = content.split('\n');
  let currentTitle = '';
  let currentContent: string[] = [];

  for (const line of lines) {
    // Check for H1 headings (# Title) - treat as section headers
    const h1Match = line.match(/^#\s+(.+)$/);
    // Check for H2 headings (## Title) - also treat as section headers
    const h2Match = line.match(/^##\s+(.+)$/);
    
    if (h1Match || h2Match) {
      // Save previous section
      if (currentTitle || currentContent.length > 0) {
        const trimmedContent = currentContent.join('\n').trim();
        if (currentTitle || trimmedContent) {
          sections.push({ title: currentTitle, content: trimmedContent });
        }
      }
      // Format the title to add spaces in CamelCase names
      const rawTitle = (h1Match ? h1Match[1] : h2Match![1]).trim();
      currentTitle = formatCamelCaseTitle(rawTitle);
      currentContent = [];
    } else {
      currentContent.push(line);
    }
  }
  
  // Save last section
  if (currentTitle || currentContent.length > 0) {
    const trimmedContent = currentContent.join('\n').trim();
    if (currentTitle || trimmedContent) {
      sections.push({ title: currentTitle, content: trimmedContent });
    }
  }

  return sections;
}

// Get section styling based on title
function getSectionStyle(title: string): { boxClass: string; bulletClass: string; borderColor?: string; bgColor?: string } {
  const lowerTitle = title.toLowerCase();
  
  if (lowerTitle.includes('key objectives') || lowerTitle.includes('objectives') || lowerTitle.includes('goals')) {
    return {
      boxClass: 'bg-emerald-50/50 border-l-4 border-emerald-500 rounded-r-lg p-5',
      bulletClass: 'text-emerald-600 font-medium',
      borderColor: '#10B981',
      bgColor: 'rgba(16, 185, 129, 0.05)',
    };
  }
  if (lowerTitle.includes('success metrics') || lowerTitle.includes('metrics')) {
    return {
      boxClass: 'bg-violet-50/50 border-l-4 border-violet-500 rounded-r-lg p-5',
      bulletClass: 'text-violet-600 font-medium',
      borderColor: '#8B5CF6',
      bgColor: 'rgba(139, 92, 246, 0.05)',
    };
  }
  if (lowerTitle.includes('timeline') || lowerTitle.includes('phases') || lowerTitle.includes('schedule') || lowerTitle.includes('stages')) {
    return {
      boxClass: 'bg-blue-50/50 border-l-4 border-blue-500 rounded-r-lg p-5',
      bulletClass: 'text-blue-600 font-medium',
      borderColor: '#3B82F6',
      bgColor: 'rgba(59, 130, 246, 0.05)',
    };
  }
  if (lowerTitle.includes('budget') || lowerTitle.includes('resources')) {
    return {
      boxClass: 'bg-amber-50/50 border-l-4 border-amber-500 rounded-r-lg p-5',
      bulletClass: 'text-amber-600 font-medium',
      borderColor: '#F59E0B',
      bgColor: 'rgba(245, 158, 11, 0.05)',
    };
  }
  if (lowerTitle.includes('risks') || lowerTitle.includes('assumptions') || lowerTitle.includes('pain') || lowerTitle.includes('challenges')) {
    return {
      boxClass: 'bg-rose-50/50 border-l-4 border-rose-500 rounded-r-lg p-5',
      bulletClass: 'text-rose-600 font-medium',
      borderColor: '#F43F5E',
      bgColor: 'rgba(244, 63, 94, 0.05)',
    };
  }
  if (lowerTitle.includes('target audience') || lowerTitle.includes('stakeholders') || lowerTitle.includes('persona')) {
    return {
      boxClass: 'bg-brand-100/50 border-l-4 border-brand-500 rounded-r-lg p-5',
      bulletClass: 'text-brand-700 font-medium',
      borderColor: '#7c5cfc',
      bgColor: 'rgba(124, 92, 252, 0.08)',
    };
  }
  if (lowerTitle.includes('summary') || lowerTitle.includes('overview') || lowerTitle.includes('executive')) {
    return {
      boxClass: 'bg-sky-50/50 border-l-4 border-sky-500 rounded-r-lg p-5',
      bulletClass: 'text-sky-600 font-medium',
      borderColor: '#0EA5E9',
      bgColor: 'rgba(14, 165, 233, 0.05)',
    };
  }
  if (lowerTitle.includes('journey') || lowerTitle.includes('flow') || lowerTitle.includes('steps') || lowerTitle.includes('touchpoint')) {
    return {
      boxClass: 'bg-teal-50/50 border-l-4 border-teal-500 rounded-r-lg p-5',
      bulletClass: 'text-teal-600 font-medium',
      borderColor: '#14B8A6',
      bgColor: 'rgba(20, 184, 166, 0.05)',
    };
  }
  if (lowerTitle.includes('details') || lowerTitle.includes('items') || lowerTitle.includes('content')) {
    return {
      boxClass: 'bg-purple-50/50 border-l-4 border-purple-500 rounded-r-lg p-5',
      bulletClass: 'text-purple-600 font-medium',
      borderColor: '#A855F7',
      bgColor: 'rgba(168, 85, 247, 0.05)',
    };
  }
  
  // Default style with subtle gray
  return { 
    boxClass: 'bg-surface-secondary/70 border-l-4 border-surface-border rounded-r-lg p-5',
    bulletClass: 'text-gray-500',
    borderColor: '#D1D5DB',
    bgColor: 'rgba(209, 213, 219, 0.1)',
  };
}

// Render a colored section
function RenderColoredSection({ title, content }: { title: string; content: string }) {
  const { boxClass, bulletClass } = getSectionStyle(title);
  
  // Skip rendering if there's no content (avoid empty colored boxes)
  if (!content || !content.trim()) {
    // Still show the title if present, but without a box
    if (title) {
      return (
        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        </section>
      );
    }
    return null;
  }

  return (
    <section className="mb-6">
      {title && <h2 className="text-lg font-semibold text-gray-900 mb-3">{title}</h2>}
      <div className={boxClass}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => <h1 className="text-2xl font-bold text-gray-900 mb-4">{children}</h1>,
            h2: ({ children }) => <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-3">{children}</h2>,
            h3: ({ children }) => <h3 className="text-base font-semibold text-gray-900 mt-4 mb-2">{children}</h3>,
            p: ({ children }) => <p className="text-gray-600 leading-relaxed mb-3 last:mb-0">{children}</p>,
            ul: ({ children }) => <ul className="space-y-2 mb-3 last:mb-0">{children}</ul>,
            ol: ({ children }) => <ol className="space-y-2 mb-3 list-decimal list-inside last:mb-0">{children}</ol>,
            li: ({ children }) => (
              <li className="text-gray-700 flex items-start gap-2">
                <span className={bulletClass}>•</span>
                <span>{children}</span>
              </li>
            ),
            strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
            table: ({ children }) => (
              <div className="overflow-x-auto mb-3">
                <table className="min-w-full border-collapse">{children}</table>
              </div>
            ),
            th: ({ children }) => <th className="border border-gray-200 px-4 py-2 bg-gray-50 font-semibold text-left text-gray-900">{children}</th>,
            td: ({ children }) => <td className="border border-gray-200 px-4 py-2 text-gray-600">{children}</td>,
            hr: () => <hr className="my-6 border-gray-200" />,
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    </section>
  );
}

// Friendly Markdown Renderer with colored sections
function FriendlyMarkdownRenderer({ content }: { content: string }) {
  const sections = parseMarkdownSections(content);
  
  return (
    <div className="prose prose-gray max-w-none">
      {sections.map((section, index) => (
        <RenderColoredSection key={index} title={section.title} content={section.content} />
      ))}
    </div>
  );
}

export default function AdminTemplatePage() {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const token = useAuthToken();
  const templateId = params.id as string;
  const isNew = templateId === 'new';
  const duplicateFromId = searchParams.get('duplicate');
  
  // Preserve list view state (e.g., showArchived) for navigation back
  const listStateRef = useRef<string>('');
  useEffect(() => {
    // On mount, check sessionStorage for the list state
    const savedState = sessionStorage.getItem('templatesListState');
    if (savedState) {
      listStateRef.current = savedState;
    }
  }, []);
  
  // Helper to build the back URL with preserved state
  const getBackUrl = (highlight?: string) => {
    const params = new URLSearchParams(listStateRef.current);
    if (highlight) {
      params.set('highlight', highlight);
    }
    const queryString = params.toString();
    return `/admin/templates${queryString ? `?${queryString}` : ''}`;
  };
  
  // Form state
  const [icon, setIcon] = useState('📄');
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [iconCategory, setIconCategory] = useState<string>('Work & Business');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<string>('PERSONA');
  const [category, setCategory] = useState<string>('discovery');
  const [instructions, setInstructions] = useState('');
  const [outputFormatSpec, setOutputFormatSpec] = useState('');
  const [version, setVersion] = useState('1.0.0');
  const [status, setStatus] = useState<string>('DRAFT');
  const [aiRole, setAiRole] = useState('UX Researcher');
  const [conversationStarters, setConversationStarters] = useState<string[]>([]);
  const [relatedTemplates, setRelatedTemplates] = useState<Array<{id: string; name: string; category: string}>>([]);
  
  // Track which fields have been touched (for validation display)
  const [touched, setTouched] = useState<{ name: boolean; instructions: boolean }>({ name: false, instructions: false });
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  // Track original values to detect changes
  const [originalValues, setOriginalValues] = useState<{
    icon: string;
    name: string;
    description: string;
    type: string;
    category: string;
    instructions: string;
    outputFormatSpec: string;
    aiRole: string;
    conversationStarters: string[];
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Preview state
  const [selectedModel, setSelectedModel] = useState('deepseek-chat');
  const [previewOutput, setPreviewOutput] = useState('');
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [testPrompt, setTestPrompt] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [previewView, setPreviewView] = useState<'friendly' | 'raw'>('friendly');
  const [copyState, setCopyState] = useState<'markdown' | 'raw' | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatPreviewOutput = (output: string) => {
    const trimmed = output.trim();
    if (!trimmed) {
      return { pretty: '', raw: output, isJson: false, parsed: null as unknown };
    }
    try {
      const parsed = JSON.parse(trimmed);
      return {
        pretty: JSON.stringify(parsed, null, 2),
        raw: output,
        isJson: true,
        parsed,
      };
    } catch {
      return { pretty: output, raw: output, isJson: false, parsed: null as unknown };
    }
  };

  const formattedPreview = useMemo(() => formatPreviewOutput(previewOutput), [previewOutput]);

  const buildFriendlyMarkdown = (parsed: any) => {
    if (!parsed || typeof parsed !== 'object') {
      return `## Preview\n\n${previewOutput.trim()}`;
    }

    if (Array.isArray(parsed.goals)) {
      const goals = parsed.goals as Array<Record<string, any>>;
      const lines = ['## Goals Summary', ''];
      if (parsed.summary) {
        lines.push(parsed.summary, '');
      }
      goals.forEach((goal, index) => {
        lines.push(`### Goal ${goal.goal_id || index + 1}`);
        if (goal.goal_statement) lines.push(`- **Goal:** ${goal.goal_statement}`);
        if (goal.domain_tag) lines.push(`- **Domain:** ${goal.domain_tag}`);
        if (goal.priority_tag) lines.push(`- **Priority:** ${goal.priority_tag}`);
        if (goal.timeline) lines.push(`- **Timeline:** ${goal.timeline}`);
        if (goal.description) lines.push(`- **Description:** ${goal.description}`);
        if (Array.isArray(goal.success_metrics) && goal.success_metrics.length > 0) {
          lines.push(`- **Success metrics:**`);
          goal.success_metrics.forEach((metric: any) => {
            const metricLine = [
              metric.metric ? metric.metric : 'Metric',
              metric.target ? `target: ${metric.target}` : null,
              metric.baseline ? `baseline: ${metric.baseline}` : null,
            ].filter(Boolean).join(' — ');
            lines.push(`  - ${metricLine}`);
          });
        }
        if (Array.isArray(goal.assumptions) && goal.assumptions.length > 0) {
          lines.push(`- **Assumptions:** ${goal.assumptions.join('; ')}`);
        }
        if (Array.isArray(goal.dependencies) && goal.dependencies.length > 0) {
          lines.push(`- **Dependencies:** ${goal.dependencies.join('; ')}`);
        }
        lines.push('');
      });
      return lines.join('\n').trim();
    }

    const entries = Object.entries(parsed).slice(0, 12);
    const lines = ['## Preview Summary', ''];
    entries.forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      if (Array.isArray(value)) {
        lines.push(`- **${key}:** ${value.length} item${value.length === 1 ? '' : 's'}`);
      } else if (typeof value === 'object') {
        lines.push(`- **${key}:** ${Object.keys(value).length} fields`);
      } else {
        lines.push(`- **${key}:** ${String(value)}`);
      }
    });
    return lines.join('\n');
  };

  const friendlyMarkdown = useMemo(() => {
    if (!formattedPreview.isJson) return '';
    return buildFriendlyMarkdown(formattedPreview.parsed);
  }, [formattedPreview]);

  // Allow Friendly view for both JSON and markdown content
  const effectivePreviewView = previewView;

  const handleCopyMarkdown = () => {
    if (!previewOutput.trim()) return;
    const markdown = formattedPreview.isJson
      ? friendlyMarkdown || `\`\`\`json\n${formattedPreview.pretty}\n\`\`\``
      : `\`\`\`\n${formattedPreview.raw}\n\`\`\``;
    navigator.clipboard.writeText(markdown)
      .then(() => {
        setCopyState('markdown');
        toast({ title: 'Copied', description: 'Preview copied as Markdown.' });
        setTimeout(() => setCopyState(null), 1200);
      })
      .catch(() => {
        toast({
          title: 'Copy failed',
          description: 'Please try again or select and copy manually.',
          variant: 'destructive',
        });
      });
  };

  const handleCopyRaw = () => {
    if (!previewOutput.trim()) return;
    const raw = formattedPreview.isJson ? formattedPreview.pretty : formattedPreview.raw;
    navigator.clipboard.writeText(raw)
      .then(() => {
        setCopyState('raw');
        toast({ title: 'Copied', description: 'Raw preview copied.' });
        setTimeout(() => setCopyState(null), 1200);
      })
      .catch(() => {
        toast({
          title: 'Copy failed',
          description: 'Please try again or select and copy manually.',
          variant: 'destructive',
        });
      });
  };

  const renderFriendlySummary = (parsed: any) => {
    if (!parsed || typeof parsed !== 'object') {
      return (
        <p className="text-sm text-gray-600">
          We could not format this output, so it is shown as-is. You can switch to Raw view to see the full content.
        </p>
      );
    }

    // Extract goals from various possible structures
    let goals: Array<Record<string, any>> | null = null;
    let summary: string | null = null;

    // Helper to check if an object looks like a goal
    const isGoalObject = (obj: any) => {
      return obj && typeof obj === 'object' && 
        (obj.goal_statement || obj.goal_id || obj.domain_tag || obj.priority_tag);
    };

    // Helper to extract goals array from a container
    const extractGoals = (container: any): Array<Record<string, any>> | null => {
      if (Array.isArray(container) && container.length > 0 && isGoalObject(container[0])) {
        return container;
      }
      return null;
    };

    // Helper to extract summary text
    const extractSummary = (container: any): string | null => {
      if (typeof container?.summary === 'string') return container.summary;
      if (typeof container?.summary?.description === 'string') return container.summary.description;
      if (typeof container?.properties?.summary === 'string') return container.properties.summary;
      if (typeof container?.properties?.summary?.description === 'string') return container.properties.summary.description;
      return null;
    };

    // Try 1: Direct access - parsed.goals
    goals = extractGoals(parsed.goals);
    if (goals) {
      summary = extractSummary(parsed);
    }

    // Try 2: parsed.properties.goals
    if (!goals && parsed.properties?.goals) {
      goals = extractGoals(parsed.properties.goals);
      if (goals) {
        summary = extractSummary(parsed) || extractSummary(parsed.properties);
      }
    }

    // Try 3: parsed.json_schema.properties.goals (wrapped in json_schema)
    if (!goals && parsed.json_schema?.properties?.goals) {
      goals = extractGoals(parsed.json_schema.properties.goals);
      if (goals) {
        summary = extractSummary(parsed) || extractSummary(parsed.json_schema) || extractSummary(parsed.json_schema.properties);
      }
    }

    // Try 4: parsed.json_schema.properties.goals.items (when items is an array of actual goal objects)
    if (!goals && parsed.json_schema?.properties?.goals?.items) {
      const items = parsed.json_schema.properties.goals.items;
      // Check if items is an array of actual goal objects
      if (Array.isArray(items) && items.length > 0 && isGoalObject(items[0])) {
        goals = items;
        summary = extractSummary(parsed) || extractSummary(parsed.json_schema) || extractSummary(parsed.json_schema.properties);
      }
    }

    // ========== INTERVIEWS DETECTION ==========
    // Helper to check if an object looks like ACTUAL interview data (not a schema definition)
    const isActualInterviewData = (obj: any) => {
      if (!obj || typeof obj !== 'object') return false;
      // Must have actual string values for key fields, not nested type definitions
      const hasActualName = typeof obj.stakeholder_name === 'string' && !obj.stakeholder_name.includes('type');
      const hasActualRole = typeof obj.stakeholder_role === 'string' && !obj.stakeholder_role.includes('type');
      const hasActualInsights = Array.isArray(obj.key_insights) && obj.key_insights.length > 0 && typeof obj.key_insights[0] === 'string';
      return (hasActualName || hasActualRole) && hasActualInsights;
    };

    // Helper to extract ACTUAL interviews array (not schema definitions)
    const extractActualInterviews = (container: any): Array<Record<string, any>> | null => {
      if (Array.isArray(container) && container.length > 0 && isActualInterviewData(container[0])) {
        return container;
      }
      return null;
    };

    // Helper to extract themes array
    const extractThemes = (container: any): Array<Record<string, any>> | null => {
      if (Array.isArray(container) && container.length > 0 && typeof container[0]?.theme === 'string') {
        return container;
      }
      return null;
    };

    // Check if this is a schema-only definition (no actual interview data)
    const isSchemaOnly = () => {
      // If we have json_schema but no actual interviews array at root level
      if (parsed.json_schema?.properties?.interviews && !parsed.interviews) {
        return true;
      }
      // If interviews exists but is a schema definition (has 'type', 'items' etc)
      if (parsed.interviews?.type === 'array' || parsed.interviews?.items) {
        return true;
      }
      return false;
    };

    let interviews: Array<Record<string, any>> | null = null;
    let themes: Array<Record<string, any>> | null = null;

    // Try to find ACTUAL interview data arrays
    interviews = extractActualInterviews(parsed.interviews);
    if (!interviews) interviews = extractActualInterviews(parsed.properties?.interviews);

    // Try to find themes - actual arrays only
    themes = extractThemes(parsed.themes);
    if (!themes) themes = extractThemes(parsed.properties?.themes);

    // ========== SCHEMA-ONLY / DRAFT INTERVIEW DOCUMENT ==========
    // If this is a schema definition with no actual data, render as a finished draft document
    if (!interviews && (isSchemaOnly() || parsed.json_schema?.properties?.interviews)) {
      // Try to extract any role/team hints from schema descriptions
      const schemaProps = parsed.json_schema?.properties?.interviews?.items?.properties;
      const extractedRole = schemaProps?.stakeholder_role?.description || 'TBD';
      const extractedTeam = schemaProps?.stakeholder_team?.description || 'TBD';
      const extractedInfluence = schemaProps?.influence?.description || 'TBD';
      const extractedInsights = schemaProps?.key_insights?.items?.description;
      const extractedPainPoints = schemaProps?.pain_points?.items?.description;
      const provenanceDesc = schemaProps?.provenance?.properties?.input_summary?.description;

      return (
        <div className="text-gray-800" style={{ lineHeight: '1.7', fontSize: '16px' }}>
          {/* Document Title */}
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', marginBottom: '24px', marginTop: '0' }}>
            🗣️ Stakeholder Interview Summary
          </h1>

          {/* Overview Section */}
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
              🧾 Overview
            </h2>
            <p style={{ fontSize: '16px', color: '#374151', marginBottom: '12px' }}>
              This is a <strong>draft interview capture</strong> prepared in advance of the stakeholder conversation.
              {provenanceDesc && ` ${provenanceDesc}`}
            </p>
            <p style={{ fontSize: '16px', color: '#374151' }}>
              The document outlines the stakeholder profile and anticipated focus areas. Actual insights and 
              pain points will be documented following the interview session.
            </p>
          </section>

          {/* Stakeholder Profile */}
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
              👤 Stakeholder Profile
            </h2>
            <div style={{ fontSize: '16px', color: '#374151' }}>
              <div style={{ marginBottom: '8px' }}><strong>Role:</strong> {extractedRole !== 'TBD' && !extractedRole.includes('e.g.,') ? extractedRole : 'TBD'}</div>
              <div style={{ marginBottom: '8px' }}><strong>Team:</strong> {extractedTeam !== 'TBD' && !extractedTeam.includes('e.g.,') ? extractedTeam : 'TBD'}</div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Influence level:</strong>{' '}
                <span style={{ 
                  padding: '2px 8px', 
                  backgroundColor: '#E0E7FF',
                  color: '#4F46E5',
                  borderRadius: '9999px',
                  fontSize: '12px'
                }}>
                  {extractedInfluence !== 'TBD' && !extractedInfluence.includes('High') ? extractedInfluence : 'TBD'}
                </span>
              </div>
              <div><strong>Interview date:</strong> TBD</div>
            </div>
          </section>

          {/* Key Insights */}
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
              💡 Key Insights
            </h2>
            <ul style={{ paddingLeft: '24px', margin: '0', listStyleType: 'disc', color: '#374151' }}>
              <li style={{ marginBottom: '8px', color: '#6B7280' }}>
                {extractedInsights && !extractedInsights.includes('pending') ? extractedInsights : 'TBD — to be captured during interview'}
              </li>
            </ul>
          </section>

          {/* Pain Points */}
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
              ⚠️ Pain Points
            </h2>
            <ul style={{ paddingLeft: '24px', margin: '0', listStyleType: 'disc', color: '#374151' }}>
              <li style={{ marginBottom: '8px', color: '#6B7280' }}>
                {extractedPainPoints && !extractedPainPoints.includes('pending') ? extractedPainPoints : 'TBD — to be captured during interview'}
              </li>
            </ul>
          </section>

          {/* Assumptions & Gaps */}
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
              🧩 Assumptions & Gaps
            </h2>
            <ul style={{ paddingLeft: '24px', margin: '0', listStyleType: 'disc', color: '#374151' }}>
              <li style={{ marginBottom: '8px' }}>Interview has not yet been conducted</li>
              <li style={{ marginBottom: '8px' }}>Stakeholder availability and preferred communication style are unknown</li>
              <li style={{ marginBottom: '8px' }}>Specific project context may need to be established during the conversation</li>
            </ul>
          </section>

          {/* Summary */}
          <section style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>
              ✅ Summary
            </h2>
            <ul style={{ paddingLeft: '24px', margin: '0', listStyleType: 'disc', color: '#374151' }}>
              <li style={{ marginBottom: '8px' }}>This stakeholder's perspective is important for understanding project requirements and constraints.</li>
              <li style={{ marginBottom: '8px' }}>The interview should focus on uncovering key insights, pain points, and success criteria.</li>
              <li style={{ marginBottom: '8px' }}>Findings will inform project prioritization and help align deliverables with stakeholder expectations.</li>
              <li style={{ marginBottom: '8px' }}>Any risks or constraints raised should be documented and escalated as needed.</li>
            </ul>
          </section>
        </div>
      );
    }

    // If we found interviews, render them
    if (interviews && interviews.length > 0) {
      // Extract summary - try actual data first, then schema description
      let interviewSummary = extractSummary(parsed) || extractSummary(parsed.properties);
      if (!interviewSummary && parsed.json_schema?.properties?.summary?.description) {
        const desc = parsed.json_schema.properties.summary.description;
        // Only use if it looks like actual summary content, not a schema description
        if (!desc.includes('Optional') && !desc.startsWith('2–4 sentence')) {
          interviewSummary = desc;
        }
      }

      return (
        <div className="text-gray-800" style={{ lineHeight: '1.6', fontSize: '16px' }}>
          {/* Document Title */}
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', marginBottom: '32px', marginTop: '0' }}>
            🗣️ Stakeholder Interviews
          </h1>

          {/* Overview Section */}
          <section style={{ marginBottom: '48px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '16px', marginTop: '32px' }}>
              🧾 Overview
            </h2>
            {interviewSummary ? (
              <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#374151', marginBottom: '0' }}>
                {interviewSummary}
              </p>
            ) : (
              <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#374151', marginBottom: '0' }}>
                This document summarizes key findings from stakeholder interviews conducted for the project.
                Each interview captures insights, pain points, and requirements from different perspectives.
              </p>
            )}
          </section>

          {/* Key Themes Section */}
          <section style={{ marginBottom: '48px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '16px', marginTop: '32px' }}>
              🌟 Key Themes
            </h2>
            {themes && themes.length > 0 ? (
              <div style={{ display: 'grid', gap: '12px' }}>
                {themes.map((theme, idx) => (
                  <div key={idx} style={{ padding: '16px', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: theme.supporting_interview_refs?.length > 0 ? '8px' : '0' }}>
                      <span style={{ fontWeight: '600', color: '#111827' }}>{theme.theme}</span>
                      {theme.frequency_hint && (
                        <span style={{ 
                          fontSize: '12px', 
                          padding: '2px 8px', 
                          backgroundColor: theme.frequency_hint === 'High' ? '#FEE2E2' : theme.frequency_hint === 'Medium' ? '#FEF3C7' : '#E0E7FF',
                          color: theme.frequency_hint === 'High' ? '#DC2626' : theme.frequency_hint === 'Medium' ? '#D97706' : '#4F46E5',
                          borderRadius: '9999px'
                        }}>
                          {theme.frequency_hint}
                        </span>
                      )}
                    </div>
                    {Array.isArray(theme.supporting_interview_refs) && theme.supporting_interview_refs.length > 0 && (
                      <p style={{ fontSize: '14px', color: '#6B7280', margin: '0' }}>
                        Supporting interviews: {theme.supporting_interview_refs.join(', ')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: '16px', color: '#6B7280', fontStyle: 'italic', margin: '0' }}>
                No recurring themes were provided.
              </p>
            )}
          </section>

          {/* Interviews Section */}
          <section style={{ marginBottom: '48px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '24px', marginTop: '32px' }}>
              👥 Interview Notes
            </h2>
            
            {interviews.map((interview, index) => (
              <div key={interview.interview_id || index} style={{ marginBottom: '32px', paddingBottom: '24px', borderBottom: index < interviews.length - 1 ? '1px solid #E5E7EB' : 'none' }}>
                {/* Interview Header - Name and Role on one line */}
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '12px', marginTop: '0' }}>
                  👤 {interview.stakeholder_name || `Stakeholder ${index + 1}`}
                  {interview.stakeholder_role && <span style={{ fontWeight: '400', color: '#4B5563' }}> — {interview.stakeholder_role}</span>}
                </h3>
                
                {/* Metadata line */}
                <div style={{ marginBottom: '16px', fontSize: '14px', color: '#4B5563', display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                  <span><strong>Team:</strong> {interview.stakeholder_team || 'Not provided'}</span>
                  <span><strong>Date:</strong> {interview.date || 'TBD'}</span>
                  <span style={{ 
                    padding: '2px 8px', 
                    backgroundColor: interview.influence === 'High' ? '#DCFCE7' : interview.influence === 'Medium' ? '#FEF3C7' : '#E0E7FF',
                    color: interview.influence === 'High' ? '#16A34A' : interview.influence === 'Medium' ? '#D97706' : '#4F46E5',
                    borderRadius: '9999px',
                    fontSize: '12px'
                  }}>
                    {interview.influence || 'Not provided'} influence
                  </span>
                </div>

                {/* Key Insights - max 4, show note if more */}
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Key insights</h4>
                  {Array.isArray(interview.key_insights) && interview.key_insights.length > 0 ? (
                    <ul style={{ paddingLeft: '24px', margin: '0', listStyleType: 'disc' }}>
                      {interview.key_insights.slice(0, 4).map((insight: string, idx: number) => (
                        <li key={idx} style={{ fontSize: '16px', lineHeight: '1.6', color: '#374151', marginBottom: '8px' }}>{insight}</li>
                      ))}
                      {interview.key_insights.length > 4 && (
                        <li style={{ fontSize: '14px', lineHeight: '1.6', color: '#6B7280', fontStyle: 'italic' }}>
                          Additional insights were provided.
                        </li>
                      )}
                    </ul>
                  ) : (
                    <p style={{ margin: '0', color: '#6B7280', fontSize: '14px' }}>Not provided</p>
                  )}
                </div>

                {/* Pain Points - max 3 */}
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Pain points</h4>
                  {Array.isArray(interview.pain_points) && interview.pain_points.length > 0 ? (
                    <ul style={{ paddingLeft: '24px', margin: '0', listStyleType: 'disc' }}>
                      {interview.pain_points.slice(0, 3).map((point: string, idx: number) => (
                        <li key={idx} style={{ fontSize: '16px', lineHeight: '1.6', color: '#374151', marginBottom: '8px' }}>{point}</li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{ margin: '0', color: '#6B7280', fontSize: '14px' }}>Not provided</p>
                  )}
                </div>

                {/* Needs */}
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Needs</h4>
                  {Array.isArray(interview.needs) && interview.needs.length > 0 ? (
                    <ul style={{ paddingLeft: '24px', margin: '0', listStyleType: 'disc' }}>
                      {interview.needs.map((need: string, idx: number) => (
                        <li key={idx} style={{ fontSize: '16px', lineHeight: '1.6', color: '#374151', marginBottom: '8px' }}>{need}</li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{ margin: '0', color: '#6B7280', fontSize: '14px' }}>Not provided</p>
                  )}
                </div>

                {/* Risks or Constraints */}
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Risks / constraints</h4>
                  {Array.isArray(interview.risks_or_constraints) && interview.risks_or_constraints.length > 0 ? (
                    <ul style={{ paddingLeft: '24px', margin: '0', listStyleType: 'disc' }}>
                      {interview.risks_or_constraints.map((risk: string, idx: number) => (
                        <li key={idx} style={{ fontSize: '16px', lineHeight: '1.6', color: '#374151', marginBottom: '8px' }}>{risk}</li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{ margin: '0', color: '#6B7280', fontSize: '14px' }}>Not provided</p>
                  )}
                </div>

                {/* Linked Goals */}
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Links to goals</h4>
                  {(Array.isArray(interview.linked_goal_ids) && interview.linked_goal_ids.length > 0) || 
                   (Array.isArray(interview.linked_goal_refs) && interview.linked_goal_refs.length > 0) ? (
                    <ul style={{ paddingLeft: '24px', margin: '0', listStyleType: 'disc' }}>
                      {interview.linked_goal_ids?.map((goalId: string, idx: number) => (
                        <li key={`id-${idx}`} style={{ fontSize: '16px', lineHeight: '1.6', color: '#374151', marginBottom: '8px' }}>{goalId}</li>
                      ))}
                      {interview.linked_goal_refs?.map((ref: any, idx: number) => (
                        <li key={`ref-${idx}`} style={{ fontSize: '16px', lineHeight: '1.6', color: '#374151', marginBottom: '8px' }}>
                          {ref.goal_statement}{ref.note && <span style={{ color: '#6B7280', fontStyle: 'italic' }}> — {ref.note}</span>}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{ margin: '0', color: '#6B7280', fontSize: '14px' }}>Not provided</p>
                  )}
                </div>

                {/* Assumptions / gaps - only show if present */}
                {Array.isArray(interview.assumptions) && interview.assumptions.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Assumptions / gaps</h4>
                    <ul style={{ paddingLeft: '24px', margin: '0', listStyleType: 'disc' }}>
                      {interview.assumptions.slice(0, 3).map((assumption: string, idx: number) => (
                        <li key={idx} style={{ fontSize: '16px', lineHeight: '1.6', color: '#374151', marginBottom: '8px' }}>{assumption}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Attachments / Evidence - combined if present */}
                {(Array.isArray(interview.attachments) && interview.attachments.length > 0) || 
                 (Array.isArray(interview.evidence_refs) && interview.evidence_refs.length > 0) ? (
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Attachments / Evidence</h4>
                    <ul style={{ paddingLeft: '24px', margin: '0', listStyleType: 'disc' }}>
                      {interview.attachments?.map((att: any, idx: number) => (
                        <li key={`att-${idx}`} style={{ fontSize: '16px', lineHeight: '1.6', color: '#374151', marginBottom: '8px' }}>
                          {att.label || att.attachment_ref} 
                          <span style={{ 
                            marginLeft: '8px',
                            padding: '2px 8px', 
                            backgroundColor: '#F3F4F6', 
                            color: '#6B7280', 
                            fontSize: '12px',
                            borderRadius: '4px'
                          }}>
                            {att.attachment_type === 'file' ? 'File' : 'URL'}
                          </span>
                        </li>
                      ))}
                      {interview.evidence_refs?.map((ref: any, idx: number) => (
                        <li key={`ref-${idx}`} style={{ fontSize: '16px', lineHeight: '1.6', color: '#374151', marginBottom: '8px' }}>
                          {ref.note || ref.ref_id}
                          <span style={{ 
                            marginLeft: '8px',
                            padding: '2px 8px', 
                            backgroundColor: '#F3F4F6', 
                            color: '#6B7280', 
                            fontSize: '12px',
                            borderRadius: '4px'
                          }}>
                            {ref.ref_type === 'file' ? 'File' : ref.ref_type === 'url' ? 'URL' : 'Note'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {/* Provenance - optional, only if present */}
                {interview.provenance && interview.provenance.creation_mode && (
                  <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px', fontSize: '14px', color: '#6B7280' }}>
                    <strong>Provenance:</strong>{' '}
                    {interview.provenance.creation_mode?.replace(/_/g, ' ')}
                    {interview.provenance.input_summary && ` — ${interview.provenance.input_summary}`}
                  </div>
                )}
              </div>
            ))}
          </section>

          {/* Summary Section */}
          <section style={{ marginTop: '48px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>✅ Summary</h2>
            <ul style={{ paddingLeft: '24px', margin: '0', listStyleType: 'disc', fontSize: '16px', color: '#374151' }}>
              <li style={{ marginBottom: '8px' }}>
                <strong>{interviews.length} stakeholder interview{interviews.length === 1 ? '' : 's'}</strong> {interviews.length === 1 ? 'was' : 'were'} conducted
                {themes && themes.length > 0 && `, revealing ${themes.length} key theme${themes.length === 1 ? '' : 's'}`}.
              </li>
              {(() => {
                // Collect all pain points across interviews
                const allPainPoints = interviews.flatMap(i => i.pain_points || []).filter(Boolean);
                const allNeeds = interviews.flatMap(i => i.needs || []).filter(Boolean);
                const highInfluence = interviews.filter(i => i.influence === 'High');
                return (
                  <>
                    {highInfluence.length > 0 && (
                      <li style={{ marginBottom: '8px' }}>
                        {highInfluence.length} high-influence stakeholder{highInfluence.length === 1 ? '' : 's'} identified: {highInfluence.map(i => i.stakeholder_name || i.stakeholder_role).join(', ')}.
                      </li>
                    )}
                    {allPainPoints.length > 0 && (
                      <li style={{ marginBottom: '8px' }}>
                        Key pain points focus on: {allPainPoints.slice(0, 2).join('; ')}{allPainPoints.length > 2 ? ` and ${allPainPoints.length - 2} more areas` : ''}.
                      </li>
                    )}
                    {allNeeds.length > 0 && (
                      <li style={{ marginBottom: '8px' }}>
                        Success looks like addressing: {allNeeds.slice(0, 2).join('; ')}{allNeeds.length > 2 ? ` plus ${allNeeds.length - 2} additional needs` : ''}.
                      </li>
                    )}
                  </>
                );
              })()}
              <li style={{ marginBottom: '8px' }}>
                These insights will guide project decisions and prioritization.
              </li>
              {interviews.some(i => !i.date || i.date === 'TBD') && (
                <li style={{ marginBottom: '8px' }}>
                  <strong>Next step:</strong> Schedule remaining interviews with stakeholders marked as TBD.
                </li>
              )}
            </ul>
          </section>
        </div>
      );
    }

    // ========== COMPETITOR ANALYSIS - DEEP CONTENT MINING ==========
    // A competitor record needs at least 2 of these fields
    const COMPETITOR_FIELDS = ['competitor_name', 'competitor_type', 'threat_level', 'market_position', 'strengths', 'market_share_estimate'];
    
    const isCompetitorObject = (obj: any): boolean => {
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
      const matchedFields = COMPETITOR_FIELDS.filter(field => obj[field] !== undefined);
      return matchedFields.length >= 2;
    };

    // Deep search function to find competitor records ANYWHERE in the structure
    const mineCompetitors = (data: any, found: Array<Record<string, any>> = []): Array<Record<string, any>> => {
      if (!data || typeof data !== 'object') return found;
      
      // If this object itself is a competitor, add it
      if (isCompetitorObject(data)) {
        found.push(data);
        return found;
      }
      
      // If it's an array, check each element
      if (Array.isArray(data)) {
        for (const item of data) {
          mineCompetitors(item, found);
        }
        return found;
      }
      
      // For objects, recurse into all values
      for (const value of Object.values(data)) {
        mineCompetitors(value, found);
      }
      
      return found;
    };

    // Deep search for any string field that looks like a summary or market definition
    const mineText = (data: any, fieldNames: string[]): string | null => {
      if (!data || typeof data !== 'object') return null;
      
      // Check direct fields first
      for (const fieldName of fieldNames) {
        if (typeof data[fieldName] === 'string' && data[fieldName].length > 20) {
          const text = data[fieldName];
          // Skip schema-like descriptions
          if (!text.includes('Optional') && !text.startsWith('List of') && !text.includes('type:')) {
            return text;
          }
        }
      }
      
      // Recurse into nested objects
      if (Array.isArray(data)) {
        for (const item of data) {
          const result = mineText(item, fieldNames);
          if (result) return result;
        }
      } else {
        for (const value of Object.values(data)) {
          if (typeof value === 'object') {
            const result = mineText(value, fieldNames);
            if (result) return result;
          }
        }
      }
      
      return null;
    };

    // Mine for competitors from the entire parsed object
    const competitors: Array<Record<string, any>> = mineCompetitors(parsed);
    
    // Extract summary and market definition from anywhere
    const competitorSummary: string | null = mineText(parsed, ['summary', 'overview', 'description']);
    const marketDefinition: string | null = mineText(parsed, ['market_definition', 'market', 'market_scope']);

    // If we found competitors, render them
    if (competitors.length > 0) {
      // Categorize by threat level for takeaways
      const highThreats = competitors.filter(c => c.threat_level === 'High');
      const mediumThreats = competitors.filter(c => c.threat_level === 'Medium');
      const directCompetitors = competitors.filter(c => c.competitor_type === 'Direct');
      const hasWeaknesses = competitors.some(c => Array.isArray(c.weaknesses_or_gaps) && c.weaknesses_or_gaps.length > 0);
      const hasDifferentiators = competitors.some(c => Array.isArray(c.differentiators_for_us) && c.differentiators_for_us.length > 0);

      return (
        <div className="text-gray-800" style={{ lineHeight: '1.7', fontSize: '16px' }}>
          {/* Document Title */}
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', marginBottom: '24px', marginTop: '0' }}>
            📊 Competitive Strategy Brief
          </h1>

          {/* Executive Summary */}
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
              🧾 Executive Summary
            </h2>
            <p style={{ fontSize: '16px', color: '#374151', marginBottom: '12px', lineHeight: '1.8' }}>
              {competitorSummary || (
                <>
                  This strategic brief profiles {competitors.length} key player{competitors.length === 1 ? '' : 's'} in the competitive landscape
                  {directCompetitors.length > 0 && `, with ${directCompetitors.length} operating as direct competitor${directCompetitors.length === 1 ? '' : 's'}`}.
                  {highThreats.length > 0 && ` Notable: ${highThreats.length} represent${highThreats.length === 1 ? 's' : ''} high-priority competitive risk.`}
                  {' '}The analysis below outlines positioning, strengths, vulnerabilities, and opportunities for differentiation.
                </>
              )}
            </p>
          </section>

          {/* Market Context */}
          {marketDefinition && (
            <section style={{ marginBottom: '32px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
                🌍 Market Context
              </h2>
              <p style={{ fontSize: '16px', color: '#374151', lineHeight: '1.8' }}>
                {marketDefinition}
              </p>
            </section>
          )}

          {/* Competitive Landscape */}
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '20px' }}>
              🎯 Competitive Landscape
            </h2>

            {competitors.map((comp, index) => {
              const compName = comp.competitor_name || `Competitor ${index + 1}`;
              const compType = comp.competitor_type?.toLowerCase() || 'competitor';
              const threatLevel = comp.threat_level || 'unassessed';
              const position = comp.market_position;
              const share = comp.market_share_estimate;
              const strengths = Array.isArray(comp.strengths) ? comp.strengths : [];
              const weaknesses = Array.isArray(comp.weaknesses_or_gaps) ? comp.weaknesses_or_gaps : [];
              const differentiators = Array.isArray(comp.differentiators_for_us) ? comp.differentiators_for_us : [];
              
              return (
                <div key={comp.competitor_id || index} style={{ marginBottom: '32px', paddingBottom: '24px', borderBottom: index < competitors.length - 1 ? '1px solid #E5E7EB' : 'none' }}>
                  {/* Competitor Name with Threat Badge */}
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '12px', marginTop: '0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {compName}
                    <span style={{ 
                      padding: '2px 10px', 
                      borderRadius: '9999px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: threatLevel === 'High' ? '#FEE2E2' : threatLevel === 'Medium' ? '#FEF3C7' : '#E0E7FF',
                      color: threatLevel === 'High' ? '#DC2626' : threatLevel === 'Medium' ? '#D97706' : '#4F46E5'
                    }}>
                      {threatLevel} threat
                    </span>
                  </h3>

                  {/* Positioning Narrative */}
                  <p style={{ fontSize: '15px', color: '#374151', marginBottom: '16px', lineHeight: '1.7' }}>
                    {compName} is a <strong>{compType}</strong> competitor
                    {position && ` positioned as ${position.toLowerCase().replace(/\.$/, '')}`}.
                    {share && share !== 'TBD' && ` They hold approximately ${share} of the market`}
                    {comp.market_share_context && ` (${comp.market_share_context.toLowerCase()})`}.
                    {!position && !share && ` Market positioning details are not yet documented.`}
                  </p>

                  {/* Strengths - narrative intro */}
                  {strengths.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <p style={{ fontSize: '15px', color: '#374151', marginBottom: '8px', fontWeight: '600' }}>
                        Where they excel:
                      </p>
                      <ul style={{ paddingLeft: '20px', margin: '0', listStyleType: 'disc', fontSize: '15px', color: '#374151' }}>
                        {strengths.map((s: string, i: number) => <li key={i} style={{ marginBottom: '4px' }}>{s}</li>)}
                      </ul>
                    </div>
                  )}

                  {/* Weaknesses - narrative intro */}
                  {weaknesses.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <p style={{ fontSize: '15px', color: '#374151', marginBottom: '8px', fontWeight: '600' }}>
                        Where they fall short:
                      </p>
                      <ul style={{ paddingLeft: '20px', margin: '0', listStyleType: 'disc', fontSize: '15px', color: '#374151' }}>
                        {weaknesses.map((w: string, i: number) => <li key={i} style={{ marginBottom: '4px' }}>{w}</li>)}
                      </ul>
                    </div>
                  )}

                  {/* Differentiation - narrative intro */}
                  {differentiators.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <p style={{ fontSize: '15px', color: '#374151', marginBottom: '8px', fontWeight: '600' }}>
                        Our differentiation opportunities:
                      </p>
                      <ul style={{ paddingLeft: '20px', margin: '0', listStyleType: 'disc', fontSize: '15px', color: '#374151' }}>
                        {differentiators.map((d: string, i: number) => <li key={i} style={{ marginBottom: '4px' }}>{d}</li>)}
                      </ul>
                    </div>
                  )}

                  {/* Sources - subtle */}
                  {Array.isArray(comp.evidence_refs) && comp.evidence_refs.length > 0 && (
                    <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '12px', fontStyle: 'italic' }}>
                      Sources: {comp.evidence_refs.map((e: any) => e.note || e.ref_id || (typeof e === 'string' ? e : 'Reference')).join(', ')}
                    </p>
                  )}
                </div>
              );
            })}
          </section>

          {/* Strategic Recommendations */}
          <section style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>
              📌 Strategic Recommendations
            </h2>
            <div style={{ fontSize: '16px', color: '#374151', lineHeight: '1.8' }}>
              {/* Priority Focus */}
              {highThreats.length > 0 && (
                <p style={{ marginBottom: '12px' }}>
                  <strong>Priority focus:</strong> {highThreats.map(c => c.competitor_name).join(' and ')} represent{highThreats.length === 1 ? 's' : ''} the most significant competitive pressure. 
                  These players should be monitored closely and factored into strategic planning.
                </p>
              )}
              
              {/* Competitive Pressure */}
              {directCompetitors.length > 0 && (
                <p style={{ marginBottom: '12px' }}>
                  The competitive landscape includes {directCompetitors.length} direct competitor{directCompetitors.length === 1 ? '' : 's'} vying for the same customer base. 
                  Differentiation and value proposition clarity will be critical for market capture.
                </p>
              )}
              
              {/* Emerging Risks */}
              {mediumThreats.length > 0 && (
                <p style={{ marginBottom: '12px' }}>
                  {mediumThreats.length} player{mediumThreats.length === 1 ? '' : 's'} currently pose{mediumThreats.length === 1 ? 's' : ''} moderate risk but could escalate with market shifts or new funding.
                </p>
              )}
              
              {/* Opportunities */}
              {(hasWeaknesses || hasDifferentiators) && (
                <p style={{ marginBottom: '12px' }}>
                  <strong>Opportunities identified:</strong>{' '}
                  {hasWeaknesses && 'Competitor gaps present openings for targeted positioning. '}
                  {hasDifferentiators && 'Clear differentiation paths have been mapped against key players.'}
                </p>
              )}
              
              {/* Conclusion */}
              <p style={{ marginTop: '16px', fontStyle: 'italic', color: '#6B7280' }}>
                This brief covers {competitors.length} competitor{competitors.length === 1 ? '' : 's'}. 
                Regular updates are recommended as market dynamics evolve.
              </p>
            </div>
          </section>
        </div>
      );
    }

    // ========== USE CASE DETECTION WITH SCHEMA-AWARE VALUE EXTRACTION ==========
    
    // Helper to extract real value from schema-like field (const, single-enum, description fallback)
    const extractValue = (field: any): string | null => {
      if (field === null || field === undefined) return null;
      if (typeof field === 'string') return field.trim() || null;
      if (typeof field === 'number' || typeof field === 'boolean') return String(field);
      if (typeof field !== 'object') return null;
      
      // Priority 1: const value
      if (field.const !== undefined) {
        if (typeof field.const === 'string') return field.const.trim() || null;
        if (Array.isArray(field.const)) return field.const.join(', ');
        return String(field.const);
      }
      
      // Priority 2: enum with exactly 1 option
      if (Array.isArray(field.enum) && field.enum.length === 1) {
        return String(field.enum[0]);
      }
      
      // Priority 3: description as fallback (often contains intended content in schemas)
      if (field.description && typeof field.description === 'string') {
        return field.description.trim() || null;
      }
      
      return null;
    };
    
    // Helper to extract array of strings from schema-like items (const arrays, items, etc.)
    const extractArrayValues = (field: any, minItems?: number): string[] => {
      if (!field) return [];
      
      // Direct array of strings
      if (Array.isArray(field)) {
        return field.map((item: any) => {
          if (typeof item === 'string') return item;
          return extractValue(item) || (typeof item === 'object' && item.action) || (typeof item === 'object' && item.step) || '';
        }).filter(Boolean);
      }
      
      // Schema object with const as array
      if (field.const && Array.isArray(field.const)) {
        return field.const.map((v: any) => String(v));
      }
      
      // Schema object with items
      if (field.items) {
        // items is an array
        if (Array.isArray(field.items)) {
          return field.items.map((item: any) => extractValue(item) || '').filter(Boolean);
        }
        // items is an object (uniform array) - check if it has const/description
        const itemValue = extractValue(field.items);
        if (itemValue) return [itemValue];
      }
      
      return [];
    };
    
    const isUseCaseObject = (obj: any): boolean => {
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
      // A use case has at least: name/title, goal/description, and ideally main_flow or steps
      const hasName = extractValue(obj.use_case_name) || extractValue(obj.name) || extractValue(obj.title);
      const hasGoal = extractValue(obj.goal) || extractValue(obj.description) || obj.main_flow || obj.steps;
      return !!(hasName && hasGoal);
    };

    const mineUseCases = (data: any, found: Array<Record<string, any>> = []): Array<Record<string, any>> => {
      if (!data || typeof data !== 'object') return found;
      
      if (isUseCaseObject(data)) {
        found.push(data);
        return found;
      }
      
      if (Array.isArray(data)) {
        for (const item of data) {
          mineUseCases(item, found);
        }
        return found;
      }
      
      // Check properties sub-objects
      if (data.properties) {
        mineUseCases(data.properties, found);
      }
      
      for (const value of Object.values(data)) {
        mineUseCases(value, found);
      }
      
      return found;
    };

    const useCases = mineUseCases(parsed);
    const useCaseSummary = extractValue(parsed.summary) || mineText(parsed, ['summary', 'overview', 'description']);

    // If we found use cases, render them as a proper Use Case document
    if (useCases.length > 0) {
      return (
        <div style={{ lineHeight: '1.7', fontSize: '16px', color: '#374151' }}>
          {/* Document Title */}
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', marginBottom: '24px', marginTop: '0' }}>
            📘 Platform Use Cases
          </h1>

          {/* Overview Section */}
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
              🧾 Overview
            </h2>
            <p style={{ fontSize: '16px', color: '#374151' }}>
              {useCaseSummary || `This document defines ${useCases.length} use case${useCases.length === 1 ? '' : 's'} that describe how users interact with the platform to accomplish their goals. Each use case includes the actor, goal, flow of interactions, and expected outcomes.`}
            </p>
          </section>

          {/* Render each use case */}
          {useCases.map((uc, index) => {
            // Extract values using schema-aware extraction
            const ucName = extractValue(uc.use_case_name) || extractValue(uc.name) || extractValue(uc.title) || `Use Case ${index + 1}`;
            const ucId = extractValue(uc.use_case_id) || extractValue(uc.id) || null;
            const actor = extractValue(uc.primary_actor) || extractValue(uc.actor) || 'Not provided';
            const priority = extractValue(uc.priority) || 'Not provided';
            const complexity = extractValue(uc.complexity) || 'Not provided';
            const goal = extractValue(uc.goal) || extractValue(uc.description) || 'Not provided';
            const scope = extractValue(uc.scope);
            const trigger = extractValue(uc.trigger);
            
            // Extract arrays with schema-aware logic
            const preconditions = extractArrayValues(uc.preconditions);
            const postconditions = extractArrayValues(uc.postconditions);
            const mainFlowRaw = uc.main_flow || uc.steps || uc.flow;
            const mainFlow = extractArrayValues(mainFlowRaw);
            const minSteps = mainFlowRaw?.minItems || 0;
            const exceptions = uc.exception_flows || uc.exceptions || uc.alternative_flows || [];
            
            // Extract optional sections
            const businessRules = extractArrayValues(uc.business_rules);
            const nfNotes = extractArrayValues(uc.non_functional) || extractArrayValues(uc.nfr);
            const assumptions = extractArrayValues(uc.assumptions);
            
            return (
              <section key={index} style={{ marginBottom: '40px', paddingBottom: '32px', borderBottom: index < useCases.length - 1 ? '1px solid #E5E7EB' : 'none' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>
                  🧩 Use Case: {ucName}{ucId ? ` (${ucId})` : ''}
                </h2>
                
                {/* Metadata */}
                <div style={{ fontSize: '15px', color: '#4B5563', marginBottom: '20px', lineHeight: '1.8' }}>
                  <div style={{ marginBottom: '4px' }}><strong>Primary actor:</strong> {actor}</div>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Priority:</strong>{' '}
                    <span style={{ 
                      padding: '2px 8px', 
                      borderRadius: '9999px',
                      fontSize: '12px',
                      backgroundColor: priority === 'High' || priority === 'Critical' ? '#FEE2E2' : priority === 'Medium' ? '#FEF3C7' : '#E0E7FF',
                      color: priority === 'High' || priority === 'Critical' ? '#DC2626' : priority === 'Medium' ? '#D97706' : '#4F46E5'
                    }}>
                      {priority}
                    </span>
                  </div>
                  <div><strong>Complexity:</strong> {complexity}</div>
                </div>

                {/* Goal */}
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>🎯 Goal</h3>
                  <p style={{ margin: '0', color: '#374151' }}>{goal}</p>
                </div>

                {/* Scope */}
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>🔍 Scope</h3>
                  <p style={{ margin: '0', color: '#374151' }}>{scope || 'Not provided'}</p>
                </div>

                {/* Preconditions */}
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>⚙️ Preconditions</h3>
                  {preconditions.length > 0 ? (
                    <ul style={{ paddingLeft: '20px', margin: '0', listStyleType: 'disc' }}>
                      {preconditions.map((pre: string, i: number) => (
                        <li key={i} style={{ marginBottom: '4px', color: '#374151' }}>{pre}</li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{ margin: '0', color: '#6B7280' }}>Not provided</p>
                  )}
                </div>

                {/* Trigger */}
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>▶️ Trigger</h3>
                  <p style={{ margin: '0', color: '#374151' }}>{trigger || 'Not provided'}</p>
                </div>

                {/* Main Flow */}
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>🔄 Main Flow</h3>
                  {mainFlow.length > 0 ? (
                    <>
                      <ol style={{ paddingLeft: '20px', margin: '0' }}>
                        {mainFlow.map((step: string, i: number) => (
                          <li key={i} style={{ marginBottom: '8px', color: '#374151' }}>{step}</li>
                        ))}
                      </ol>
                      {/* If minItems implies more steps but we only have fewer, add TBD note */}
                      {minSteps > mainFlow.length && (
                        <p style={{ margin: '8px 0 0 0', color: '#6B7280', fontStyle: 'italic' }}>
                          Additional steps: TBD ({minSteps - mainFlow.length} more expected)
                        </p>
                      )}
                    </>
                  ) : (
                    <p style={{ margin: '0', color: '#6B7280' }}>Not provided</p>
                  )}
                </div>

                {/* Postconditions */}
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>✅ Postconditions</h3>
                  {postconditions.length > 0 ? (
                    <ul style={{ paddingLeft: '20px', margin: '0', listStyleType: 'disc' }}>
                      {postconditions.map((post: string, i: number) => (
                        <li key={i} style={{ marginBottom: '4px', color: '#374151' }}>{post}</li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{ margin: '0', color: '#6B7280' }}>Not provided</p>
                  )}
                </div>

                {/* Exception Flows */}
                {Array.isArray(exceptions) && exceptions.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>⚠️ Exception Flows</h3>
                    {exceptions.map((exc: any, i: number) => {
                      const excName = extractValue(exc.exception_name) || extractValue(exc.name) || `Exception ${i + 1}`;
                      const branchPoint = extractValue(exc.branch_point);
                      const condition = extractValue(exc.condition);
                      const outcome = extractValue(exc.outcome);
                      const recovery = extractValue(exc.recovery) || extractValue(exc.recovery_notes);
                      const excSteps = extractArrayValues(exc.steps);
                      
                      return (
                        <div key={i} style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#FEF3C7', borderRadius: '8px', borderLeft: '4px solid #D97706' }}>
                          <div style={{ fontWeight: '600', color: '#92400E', marginBottom: '8px' }}>
                            {excName}
                          </div>
                          {branchPoint && (
                            <div style={{ fontSize: '14px', color: '#78350F', marginBottom: '4px' }}>
                              <strong>Branch point:</strong> {branchPoint}
                            </div>
                          )}
                          {condition && (
                            <div style={{ fontSize: '14px', color: '#78350F', marginBottom: '4px' }}>
                              <strong>Condition:</strong> {condition}
                            </div>
                          )}
                          {excSteps.length > 0 && (
                            <ol style={{ paddingLeft: '20px', margin: '8px 0', fontSize: '14px', color: '#78350F' }}>
                              {excSteps.map((step: string, j: number) => (
                                <li key={j} style={{ marginBottom: '4px' }}>{step}</li>
                              ))}
                            </ol>
                          )}
                          {outcome && (
                            <div style={{ fontSize: '14px', color: '#78350F', marginBottom: '4px' }}>
                              <strong>Outcome:</strong> {outcome}
                            </div>
                          )}
                          {recovery && (
                            <div style={{ fontSize: '14px', color: '#78350F', fontStyle: 'italic' }}>
                              <strong>Recovery:</strong> {recovery}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Rules & Non-functional Notes (optional) */}
                {(businessRules.length > 0 || nfNotes.length > 0) && (
                  <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>📌 Rules & Non-functional Notes</h3>
                    {businessRules.length > 0 && (
                      <>
                        <p style={{ margin: '0 0 4px 0', fontWeight: '500', color: '#374151' }}>Business rules:</p>
                        <ul style={{ paddingLeft: '20px', margin: '0 0 12px 0', listStyleType: 'disc' }}>
                          {businessRules.map((rule: string, i: number) => (
                            <li key={i} style={{ marginBottom: '4px', color: '#374151' }}>{rule}</li>
                          ))}
                        </ul>
                      </>
                    )}
                    {nfNotes.length > 0 && (
                      <>
                        <p style={{ margin: '0 0 4px 0', fontWeight: '500', color: '#374151' }}>Non-functional requirements:</p>
                        <ul style={{ paddingLeft: '20px', margin: '0', listStyleType: 'disc' }}>
                          {nfNotes.map((note: string, i: number) => (
                            <li key={i} style={{ marginBottom: '4px', color: '#374151' }}>{note}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                )}

                {/* Assumptions (optional) */}
                {assumptions.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>🧾 Assumptions</h3>
                    <ul style={{ paddingLeft: '20px', margin: '0', listStyleType: 'disc' }}>
                      {assumptions.map((a: string, i: number) => (
                        <li key={i} style={{ marginBottom: '4px', color: '#374151' }}>{a}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Notes */}
                {extractValue(uc.provenance?.input_summary) && (
                  <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#F3F4F6', borderRadius: '8px', fontSize: '14px', color: '#6B7280' }}>
                    <strong>🧾 Notes:</strong> {extractValue(uc.provenance.input_summary)}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      );
    }

    // ========== PERSONA DETECTION ==========
    const isPersonaObject = (obj: any): boolean => {
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
      // A persona has at least: name, role/title, and goals or challenges
      return (obj.persona_name || obj.name) && 
             (obj.role_title || obj.role || obj.title) &&
             (obj.goals || obj.primary_goals || obj.challenges || obj.key_challenges);
    };

    const minePersonas = (data: any, found: Array<Record<string, any>> = []): Array<Record<string, any>> => {
      if (!data || typeof data !== 'object') return found;
      
      if (isPersonaObject(data)) {
        found.push(data);
        return found;
      }
      
      if (Array.isArray(data)) {
        for (const item of data) {
          minePersonas(item, found);
        }
        return found;
      }
      
      for (const value of Object.values(data)) {
        minePersonas(value, found);
      }
      
      return found;
    };

    const personas = minePersonas(parsed);
    const personaSummary = mineText(parsed, ['summary', 'overview', 'description']);
    const platformContext = parsed.platform_context || parsed.context || {};

    // If we found personas, render them as a proper Persona document
    if (personas.length > 0) {
      return (
        <div style={{ lineHeight: '1.7', fontSize: '16px', color: '#374151' }}>
          {/* Document Title */}
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', marginBottom: '24px', marginTop: '0' }}>
            👥 Personas
          </h1>

          {/* Overview Section */}
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
              🧾 Overview
            </h2>
            <p style={{ fontSize: '16px', color: '#374151' }}>
              {personaSummary || `This document profiles ${personas.length} persona${personas.length === 1 ? '' : 's'} representing the key users and stakeholders of the platform. These personas help product, design, and engineering teams understand who they're building for and what matters most to each user type.`}
            </p>
          </section>

          {/* Platform Context - written as a narrative paragraph */}
          {(platformContext.platform_type || platformContext.target_org_size || platformContext.regions) && (
            <section style={{ marginBottom: '32px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
                🌍 Platform Context
              </h2>
              <p style={{ fontSize: '16px', color: '#374151', margin: '0' }}>
                {(() => {
                  const parts = [];
                  if (platformContext.platform_type) {
                    parts.push(`This is a ${platformContext.platform_type.toLowerCase()} platform`);
                  }
                  if (platformContext.target_org_size) {
                    parts.push(`designed for ${platformContext.target_org_size.toLowerCase()} organizations`);
                  }
                  if (platformContext.regions) {
                    const regionText = Array.isArray(platformContext.regions) ? platformContext.regions.join(', ') : platformContext.regions;
                    parts.push(`operating in ${regionText}`);
                  }
                  return parts.length > 0 ? parts.join(' ') + '.' : 'Platform context not specified.';
                })()}
              </p>
            </section>
          )}

          {/* Render each persona */}
          {personas.map((persona, index) => {
            const personaName = persona.persona_name || persona.name || `Persona ${index + 1}`;
            const roleTitle = persona.role_title || persona.role || persona.title || 'User';
            const personaType = persona.persona_type || persona.type || '';
            const goals = persona.goals || persona.primary_goals || [];
            const challenges = persona.challenges || persona.key_challenges || persona.pain_points || [];
            const responsibilities = persona.responsibilities || [];
            const successMetrics = persona.success_metrics || persona.how_success_measured || [];
            const usagePatterns = persona.usage_patterns || {};
            const scenarios = persona.key_scenarios || persona.scenarios || [];
            const access = persona.access_authority || persona.permissions || null;
            const evidence = persona.evidence || persona.research || null;
            const assumptions = persona.assumptions || persona.notes || [];

            return (
              <section key={index} style={{ marginBottom: '48px', paddingBottom: '32px', borderBottom: index < personas.length - 1 ? '1px solid #E5E7EB' : 'none' }}>
                <h2 style={{ fontSize: '22px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                  👤 {personaName}
                </h2>
                <p style={{ fontSize: '15px', color: '#6B7280', fontStyle: 'italic', marginTop: '0', marginBottom: '20px' }}>
                  {roleTitle}{personaType && ` · ${personaType}`}
                </p>

                {/* Snapshot - written as a human story */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>🧠 Snapshot</h3>
                  <p style={{ margin: '0', color: '#374151' }}>
                    {persona.snapshot || persona.description || persona.demographics_summary || 
                     `${personaName} serves as a ${roleTitle}${persona.org_context || persona.team ? ` within ${persona.org_context || persona.team}` : ''}.`
                    }
                  </p>
                </div>

                {/* Goals */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>🎯 Goals</h3>
                  {Array.isArray(goals) && goals.length > 0 ? (
                    <ul style={{ paddingLeft: '20px', margin: '0', listStyleType: 'disc' }}>
                      {goals.map((goal: any, i: number) => (
                        <li key={i} style={{ marginBottom: '6px', color: '#374151' }}>
                          {typeof goal === 'string' ? goal : goal.goal || goal.description || goal.statement || 'Goal'}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{ margin: '0', color: '#6B7280' }}>Not provided</p>
                  )}
                </div>

                {/* Challenges */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>⚠️ Challenges</h3>
                  {Array.isArray(challenges) && challenges.length > 0 ? (
                    <ul style={{ paddingLeft: '20px', margin: '0', listStyleType: 'disc' }}>
                      {challenges.map((challenge: any, i: number) => (
                        <li key={i} style={{ marginBottom: '6px', color: '#374151' }}>
                          {typeof challenge === 'string' ? challenge : challenge.challenge || challenge.description || challenge.pain_point || 'Challenge'}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{ margin: '0', color: '#6B7280' }}>Not provided</p>
                  )}
                </div>

                {/* How Success Is Measured - only if provided */}
                {Array.isArray(successMetrics) && successMetrics.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>📊 How Success Is Measured</h3>
                    <ul style={{ paddingLeft: '20px', margin: '0', listStyleType: 'disc' }}>
                      {successMetrics.map((metric: any, i: number) => (
                        <li key={i} style={{ marginBottom: '6px', color: '#374151' }}>
                          {typeof metric === 'string' ? metric : metric.metric || metric.description || 'Metric'}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Responsibilities - only if provided */}
                {Array.isArray(responsibilities) && responsibilities.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>🧩 Responsibilities</h3>
                    <ul style={{ paddingLeft: '20px', margin: '0', listStyleType: 'disc' }}>
                      {responsibilities.map((resp: any, i: number) => (
                        <li key={i} style={{ marginBottom: '6px', color: '#374151' }}>
                          {typeof resp === 'string' ? resp : resp.responsibility || resp.description || 'Responsibility'}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Access & Authority - only if provided */}
                {access && (
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>🔐 Access & Authority</h3>
                    <p style={{ margin: '0', color: '#374151' }}>
                      {typeof access === 'string' ? access : access.description || access.permissions || 'Standard access level'}
                    </p>
                  </div>
                )}

                {/* Usage Patterns - only if provided */}
                {(usagePatterns.frequency || usagePatterns.channels || usagePatterns.usage_frequency) && (
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>🔄 Usage Patterns</h3>
                    <div style={{ fontSize: '15px', color: '#374151' }}>
                      {(usagePatterns.frequency || usagePatterns.usage_frequency) && (
                        <div style={{ marginBottom: '4px' }}><strong>Frequency:</strong> {usagePatterns.frequency || usagePatterns.usage_frequency}</div>
                      )}
                      {usagePatterns.channels && (
                        <div><strong>Primary channels:</strong> {Array.isArray(usagePatterns.channels) ? usagePatterns.channels.join(', ') : usagePatterns.channels}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Key Scenarios - only if provided */}
                {Array.isArray(scenarios) && scenarios.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>🧭 Key Scenarios</h3>
                    {scenarios.map((scenario: any, i: number) => {
                      const title = scenario.title || scenario.scenario_name || scenario.name || `Scenario ${i + 1}`;
                      const trigger = scenario.trigger;
                      const outcome = scenario.desired_outcome || scenario.outcome;
                      const steps = scenario.happy_path || scenario.steps || [];
                      
                      return (
                        <div key={i} style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                          <div style={{ fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                            {title}
                          </div>
                          {trigger && (
                            <p style={{ fontSize: '14px', color: '#374151', margin: '0 0 4px 0' }}>
                              <strong>Trigger:</strong> {trigger}
                            </p>
                          )}
                          {outcome && (
                            <p style={{ fontSize: '14px', color: '#374151', margin: '0 0 8px 0' }}>
                              <strong>Desired outcome:</strong> {outcome}
                            </p>
                          )}
                          {Array.isArray(steps) && steps.length > 0 && (
                            <ol style={{ paddingLeft: '20px', margin: '8px 0 0 0', fontSize: '14px', color: '#374151' }}>
                              {steps.map((step: any, j: number) => (
                                <li key={j} style={{ marginBottom: '4px' }}>
                                  {typeof step === 'string' ? step : step.action || step.step || 'TBD'}
                                </li>
                              ))}
                            </ol>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Evidence & Confidence - only if provided */}
                {evidence && (
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>🧪 Evidence & Confidence</h3>
                    <div style={{ fontSize: '15px', color: '#374151' }}>
                      {evidence.level && (
                        <div style={{ marginBottom: '4px' }}>
                          <strong>Evidence level:</strong>{' '}
                          <span style={{ 
                            padding: '2px 8px', 
                            borderRadius: '9999px',
                            fontSize: '12px',
                            backgroundColor: evidence.level === 'Confirmed' ? '#DCFCE7' : evidence.level === 'Partial' ? '#FEF3C7' : '#E0E7FF',
                            color: evidence.level === 'Confirmed' ? '#16A34A' : evidence.level === 'Partial' ? '#D97706' : '#4F46E5'
                          }}>
                            {evidence.level}
                          </span>
                        </div>
                      )}
                      {evidence.references && (
                        <div><strong>Research references:</strong> {Array.isArray(evidence.references) ? evidence.references.join(', ') : evidence.references}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes & Assumptions - only if provided */}
                {Array.isArray(assumptions) && assumptions.length > 0 && (
                  <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#F3F4F6', borderRadius: '8px', fontSize: '14px', color: '#6B7280' }}>
                    <strong>🧾 Notes & Assumptions:</strong>
                    <ul style={{ paddingLeft: '20px', margin: '8px 0 0 0' }}>
                      {assumptions.slice(0, 3).map((note: any, i: number) => (
                        <li key={i} style={{ marginBottom: '4px' }}>
                          {typeof note === 'string' ? note : note.assumption || note.note || 'Note'}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      );
    }

    // If we found goals, render them
    if (goals && goals.length > 0) {
        
        // Group goals by priority for summary
        const criticalGoals = goals.filter(g => g.priority_tag === 'Critical');
        const highGoals = goals.filter(g => g.priority_tag === 'High');

      // Domain emoji mapping
      const domainEmojis: Record<string, string> = {
        'Technical': '🛠️',
        'Business': '📈',
        'User': '👤',
        'Product': '📦',
        'Risk/Compliance': '🛡️'
      };

      return (
        <div className="text-gray-800" style={{ lineHeight: '1.6', fontSize: '16px' }}>
          {/* Document Title */}
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', marginBottom: '32px', marginTop: '0' }}>
            🎯 Project Goals & Objectives
          </h1>

          {/* Project Overview Section */}
          <section style={{ marginBottom: '48px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '16px', marginTop: '32px' }}>
              🌿 Project Overview
            </h2>
            {summary ? (
              <div>
                {summary.split('\n').filter((p: string) => p.trim()).map((para: string, idx: number) => (
                  <p key={idx} style={{ fontSize: '16px', lineHeight: '1.6', color: '#374151', marginBottom: '12px', marginTop: '0' }}>
                    {para}
                  </p>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#374151', marginBottom: '0' }}>
                This document outlines the strategic goals and objectives for the project. 
                Each goal has been carefully defined with clear success criteria, timelines, and dependencies 
                to ensure measurable outcomes and successful delivery.
              </p>
            )}
          </section>

          {/* Project Goals Section */}
          <section style={{ marginBottom: '48px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '24px', marginTop: '32px' }}>
              🚀 Project Goals
            </h2>
            
            {goals.map((goal, index) => {
              const goalNumber = index + 1;
              
              return (
                <div key={goal.goal_id || index} style={{ marginBottom: '32px', paddingBottom: '24px', borderBottom: index < goals.length - 1 ? '1px solid #E5E7EB' : 'none' }}>
                  {/* Goal Title */}
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '8px', marginTop: '0' }}>
                    {domainEmojis[goal.domain_tag || ''] || '🎯'} Goal {goalNumber}: {goal.goal_statement || `Project Goal ${goalNumber}`}
                  </h3>
                  
                  <div style={{ marginBottom: '16px', fontSize: '14px', color: '#4B5563' }}>
                    {goal.priority_tag && (
                      <span style={{ marginRight: '16px' }}>
                        <strong>Priority:</strong> {
                          goal.priority_tag === 'Critical' ? '🔴 ' :
                          goal.priority_tag === 'High' ? '🟠 ' :
                          goal.priority_tag === 'Medium' ? '🟡 ' :
                          goal.priority_tag === 'Low' ? '🟢 ' : ''
                        }{goal.priority_tag}
                      </span>
                    )}
                    {goal.domain_tag && (
                      <span>
                        <strong>Domain:</strong> {goal.domain_tag}
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  {goal.description && (
                    <div style={{ marginBottom: '16px' }}>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Description:</h4>
                      <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#374151', margin: '0' }}>
                        {goal.description}
                      </p>
                    </div>
                  )}

                  {/* Success Criteria */}
                  {Array.isArray(goal.success_metrics) && goal.success_metrics.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Success Criteria:</h4>
                      <ul style={{ paddingLeft: '24px', margin: '0', listStyleType: 'disc' }}>
                        {goal.success_metrics.map((metric: any, idx: number) => {
                          // Format metric text naturally
                          let metricText = '';
                          if (metric.metric && metric.target) {
                            metricText = `${metric.metric}, with a target of ${metric.target}`;
                          } else if (metric.metric) {
                            metricText = metric.metric;
                          } else if (metric.target) {
                            metricText = `Target: ${metric.target}`;
                          }
                          
                          return (
                            <li key={idx} style={{ fontSize: '16px', lineHeight: '1.6', color: '#374151', marginBottom: '8px' }}>
                              {metricText || `Metric ${idx + 1}`}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {/* Timeline */}
                  {goal.timeline && (
                    <div style={{ marginBottom: '16px' }}>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Timeline:</h4>
                      <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#374151', margin: '0' }}>
                        🗓️ {goal.timeline}
                      </p>
                    </div>
                  )}

                  {/* Key Assumptions */}
                  {Array.isArray(goal.assumptions) && goal.assumptions.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Key Assumptions:</h4>
                      <ul style={{ paddingLeft: '24px', margin: '0', listStyleType: 'disc' }}>
                        {goal.assumptions.map((a: string, idx: number) => (
                          <li key={idx} style={{ fontSize: '16px', lineHeight: '1.6', color: '#374151', marginBottom: '8px' }}>{a}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Dependencies */}
                  {Array.isArray(goal.dependencies) && goal.dependencies.length > 0 && (
                    <div>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Dependencies:</h4>
                      <ul style={{ paddingLeft: '24px', margin: '0', listStyleType: 'disc' }}>
                        {goal.dependencies.map((d: string, idx: number) => (
                          <li key={idx} style={{ fontSize: '16px', lineHeight: '1.6', color: '#374151', marginBottom: '8px' }}>{d}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </section>

          {/* Summary Section */}
          <section style={{ marginTop: '48px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>📝 Summary</h2>
            <div>
              <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#374151', marginBottom: '12px', marginTop: '0' }}>
                This project encompasses <strong>{goals.length} strategic goal{goals.length === 1 ? '' : 's'}</strong>
                {criticalGoals.length > 0 && `, with ${criticalGoals.length} critical priorit${criticalGoals.length === 1 ? 'y' : 'ies'}`}
                {highGoals.length > 0 && ` and ${highGoals.length} high-priority initiative${highGoals.length === 1 ? '' : 's'}`}.
              </p>
              <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#374151', marginBottom: '12px', marginTop: '0' }}>
                Each goal has been designed with clear success criteria and realistic timelines to ensure measurable outcomes. 
                The structured approach outlined in this document provides a solid foundation for execution, 
                enabling teams to work toward shared objectives with clear accountability and transparency.
              </p>
              {summary && goals.length > 0 && (
                <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#4B5563', fontStyle: 'italic', marginTop: '0', marginBottom: '0' }}>
                  The goals outlined above work collectively to achieve the strategic vision: {summary.split('.')[0]}.
                </p>
              )}
            </div>
          </section>
        </div>
      );
    }

    // ========== UNIVERSAL DOCUMENT RENDERER (FALLBACK) ==========
    // Detect artifact type from various fields
    const rawArtifactType = parsed.artifact_type || parsed.type || 
      parsed.json_schema?.title || 'Document';
    const artifactType = formatCamelCaseTitle(rawArtifactType);

    // Helper to convert keys to readable titles
    const toReadableTitle = (key: string): string => {
      return formatCamelCaseTitle(key);
    };
    
    // Color palette for rotating section colors
    const sectionColors = [
      { borderColor: '#3B82F6', bgColor: 'rgba(59, 130, 246, 0.05)' }, // blue
      { borderColor: '#10B981', bgColor: 'rgba(16, 185, 129, 0.05)' }, // emerald
      { borderColor: '#8B5CF6', bgColor: 'rgba(139, 92, 246, 0.05)' }, // violet
      { borderColor: '#F59E0B', bgColor: 'rgba(245, 158, 11, 0.05)' }, // amber
      { borderColor: '#14B8A6', bgColor: 'rgba(20, 184, 166, 0.05)' }, // teal
      { borderColor: '#EC4899', bgColor: 'rgba(236, 72, 153, 0.05)' }, // pink
    ];

    // Helper to render any value as human-readable text (NEVER JSON)
    const renderValue = (value: any, depth: number = 0): React.ReactNode => {
      if (value === null || value === undefined) return 'TBD';
      if (typeof value === 'string') return value || 'TBD';
      if (typeof value === 'number' || typeof value === 'boolean') return String(value);
      
      if (Array.isArray(value)) {
        if (value.length === 0) return 'None provided';
        // Render array of strings as bullet list
        if (typeof value[0] === 'string') {
          return (
            <ul style={{ paddingLeft: '20px', margin: '4px 0', listStyleType: 'disc' }}>
              {value.map((item, i) => (
                <li key={i} style={{ marginBottom: '4px', color: '#374151' }}>{item}</li>
              ))}
            </ul>
          );
        }
        // Render array of objects - extract meaningful text
        return (
          <ul style={{ paddingLeft: '20px', margin: '4px 0', listStyleType: 'disc' }}>
            {value.map((item, i) => {
              if (typeof item === 'object') {
                // Extract first meaningful string value
                const textValue = item.name || item.title || item.statement || item.description || 
                  item.goal_statement || item.metric || item.theme || 
                  Object.values(item).find(v => typeof v === 'string' && v.length > 0);
                return <li key={i} style={{ marginBottom: '4px', color: '#374151' }}>{textValue || 'Item ' + (i + 1)}</li>;
              }
              return <li key={i} style={{ marginBottom: '4px', color: '#374151' }}>{String(item)}</li>;
            })}
          </ul>
        );
      }
      
      if (typeof value === 'object') {
        // Skip schema-like objects
        if (value.type && (value.properties || value.items)) return null;
        
        // Extract meaningful text from object
        const textValue = value.name || value.title || value.description || value.statement ||
          value.goal_statement || value.summary || value.note;
        if (textValue) return textValue;
        
        // Render simple key-value pairs
        const entries = Object.entries(value).filter(([k, v]) => 
          v !== null && v !== undefined && 
          !['type', 'properties', 'items', 'required', '$schema', 'enum'].includes(k)
        );
        if (entries.length === 0) return 'TBD';
        
        if (depth < 2) {
          return (
            <div style={{ marginLeft: depth > 0 ? '16px' : '0' }}>
              {entries.slice(0, 5).map(([k, v]) => (
                <div key={k} style={{ marginBottom: '4px' }}>
                  <strong>{toReadableTitle(k)}:</strong> {renderValue(v, depth + 1)}
                </div>
              ))}
            </div>
          );
        }
        return entries.map(([k, v]) => `${toReadableTitle(k)}: ${typeof v === 'string' ? v : 'See details'}`).join('; ');
      }
      
      return String(value);
    };

    // Find all meaningful content records in the parsed data
    const findRecords = (data: any, found: Array<Record<string, any>> = []): Array<Record<string, any>> => {
      if (!data || typeof data !== 'object') return found;
      
      // Check if this looks like a content record (has multiple meaningful fields)
      const meaningfulFields = ['name', 'title', 'statement', 'description', 'summary', 'insights', 
        'strengths', 'role', 'goal_statement', 'stakeholder_name', 'competitor_name'];
      const hasContent = meaningfulFields.some(f => data[f] && typeof data[f] === 'string');
      
      if (hasContent && Object.keys(data).length >= 2) {
        found.push(data);
      }
      
      // Recurse into arrays and objects
      if (Array.isArray(data)) {
        data.forEach(item => findRecords(item, found));
      } else {
        Object.values(data).forEach(value => {
          if (typeof value === 'object') findRecords(value, found);
        });
      }
      
      return found;
    };

    const records = findRecords(parsed);

    // Render as universal document
    return (
      <div style={{ lineHeight: '1.7', fontSize: '16px', color: '#374151' }}>
        {/* Document Title */}
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', marginBottom: '24px', marginTop: '0' }}>
          📄 {toReadableTitle(artifactType)}
        </h1>

        {/* Overview Section */}
        <section style={{ marginBottom: '32px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
            Executive Summary
          </h2>
          <div style={{ 
            borderLeft: '4px solid #0EA5E9', 
            backgroundColor: 'rgba(14, 165, 233, 0.05)', 
            borderRadius: '0 8px 8px 0', 
            padding: '16px 20px' 
          }}>
            {parsed.summary && typeof parsed.summary === 'string' ? (
              <p style={{ fontSize: '16px', color: '#374151', margin: 0 }}>{parsed.summary}</p>
            ) : records.length > 0 ? (
              <p style={{ fontSize: '16px', color: '#374151', margin: 0 }}>
                This document outlines <strong>{records.length} {records.length === 1 ? 'item' : 'items'}</strong> related to {artifactType.toLowerCase()}.
                Each section below provides details that support decision-making and planning.
              </p>
            ) : (
              <p style={{ fontSize: '16px', color: '#374151', margin: 0 }}>
                This is a <strong>draft</strong> for {artifactType.toLowerCase()}. 
                Content will be added as it becomes available.
              </p>
            )}
          </div>
        </section>

        {/* Content Sections */}
        {records.length > 0 && (
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>
              Details
            </h2>
            {records.map((record, index) => {
              // Find a meaningful title from the record
              const recordTitle = record.name || record.title || record.goal_statement || 
                record.stakeholder_name || record.competitor_name || record.use_case_name ||
                record.persona_name || record.statement || `Item ${index + 1}`;
              const entries = Object.entries(record).filter(([k, v]) => 
                v !== null && v !== undefined && v !== '' &&
                !['type', 'properties', 'items', 'required', '$schema', 'enum', 'const'].includes(k)
              );
              
              // Get rotating color for this record
              const colorScheme = sectionColors[index % sectionColors.length];
              
              return (
                <div 
                  key={index} 
                  style={{ 
                    marginBottom: '20px',
                    borderLeft: `4px solid ${colorScheme.borderColor}`,
                    backgroundColor: colorScheme.bgColor,
                    borderRadius: '0 8px 8px 0',
                    padding: '16px 20px',
                  }}
                >
                  <h3 style={{ fontSize: '17px', fontWeight: '600', color: '#111827', marginBottom: '12px', marginTop: '0' }}>
                    {formatCamelCaseTitle(recordTitle)}
                  </h3>
                  {entries.map(([key, value]) => {
                    // Skip title fields already shown in header
                    if (['name', 'title', 'goal_statement', 'stakeholder_name', 'competitor_name'].includes(key) && 
                        value === recordTitle) return null;
                    
                    return (
                      <div key={key} style={{ marginBottom: '6px' }}>
                        <strong style={{ color: '#374151', fontFamily: 'ui-monospace, monospace', fontSize: '13px' }}>{toReadableTitle(key)}:</strong>{' '}
                        <span style={{ color: '#4B5563' }}>{renderValue(value)}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </section>
        )}

        {/* If no records found, show template structure */}
        {records.length === 0 && (
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>
              Assumptions & Gaps
            </h2>
            <div style={{ 
              borderLeft: '4px solid #F43F5E', 
              backgroundColor: 'rgba(244, 63, 94, 0.05)', 
              borderRadius: '0 8px 8px 0', 
              padding: '16px 20px' 
            }}>
              <ul style={{ paddingLeft: '20px', margin: '0', listStyleType: 'disc', color: '#4B5563' }}>
                <li style={{ marginBottom: '8px' }}>No concrete data records were found in the input</li>
                <li style={{ marginBottom: '8px' }}>This document serves as a template for future content</li>
                <li style={{ marginBottom: '8px' }}>Actual values will be populated as they become available</li>
              </ul>
            </div>
          </section>
        )}

        {/* Summary */}
        <section style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>
            ✅ Summary
          </h2>
          <ul style={{ paddingLeft: '24px', margin: '0', listStyleType: 'disc' }}>
            <li style={{ marginBottom: '8px' }}>
              Document type: <strong>{toReadableTitle(artifactType)}</strong>
            </li>
            {records.length > 0 && (
              <li style={{ marginBottom: '8px' }}>
                Covers {records.length} {records.length === 1 ? 'item' : 'items'} as detailed above
              </li>
            )}
            <li style={{ marginBottom: '8px' }}>
              {records.length > 0 
                ? 'All relevant information is presented in the sections above'
                : 'Ready to be completed with additional information'
              }
            </li>
          </ul>
        </section>
      </div>
    );
  };

  // Form validation
  const isFormValid = useMemo(() => {
    return !!(name.trim() && category && type && instructions.trim());
  }, [name, category, type, instructions]);

  // Detect if form has changes (for existing templates)
  const hasChanges = useMemo(() => {
    // New templates always have "changes" (need to be created)
    if (isNew) return true;
    // If no original values loaded yet, no changes
    if (!originalValues) return false;
    
    // Compare current values with original
    const startersChanged = JSON.stringify(conversationStarters.filter(s => s.trim())) !== 
                           JSON.stringify(originalValues.conversationStarters.filter(s => s.trim()));
    
    return (
      icon !== originalValues.icon ||
      name !== originalValues.name ||
      description !== originalValues.description ||
      type !== originalValues.type ||
      category !== originalValues.category ||
      instructions !== originalValues.instructions ||
      outputFormatSpec !== originalValues.outputFormatSpec ||
      aiRole !== originalValues.aiRole ||
      startersChanged
    );
  }, [isNew, originalValues, icon, name, description, type, category, instructions, outputFormatSpec, aiRole, conversationStarters]);

  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    if (!name.trim()) errors.push('Name is required');
    if (!instructions.trim()) errors.push('Instructions are required');
    return errors;
  }, [name, instructions]);

  // Fetch template data
  const { data: template, isLoading: isTemplateLoading, isError: isTemplateError } = useTemplate(templateId, {
    enabled: !isNew,
  });

  // Fetch available templates for related templates
  const { data: templatesData } = useTemplates({ pageSize: 100 });
  const availableTemplates = useMemo(() => {
    return (templatesData?.templates || [])
      .filter(t => t.id !== templateId)
      .map(t => ({ 
        id: t.id, 
        name: t.name, 
        category: TEMPLATE_CATEGORY_LABELS[t.category as keyof typeof TEMPLATE_CATEGORY_LABELS] || t.category 
      }));
  }, [templatesData, templateId]);
  
  const filteredTemplates = availableTemplates.filter(t => 
    !relatedTemplates.find(rt => rt.id === t.id) &&
    t.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Mutations
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate();

  // Fetch source template when duplicating
  useEffect(() => {
    if (isNew && duplicateFromId && token) {
      fetch(`/api/templates/${duplicateFromId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(({ data: sourceTemplate }) => {
          if (sourceTemplate) {
            setIcon(sourceTemplate.icon || '📄');
            setName(`${sourceTemplate.name} (Copy)`);
            setDescription(sourceTemplate.description || '');
            setType(sourceTemplate.type);
            setCategory(sourceTemplate.category);
            setInstructions(sourceTemplate.instructions || '');
            setOutputFormatSpec(sourceTemplate.outputFormatSpec || '');
            setConversationStarters(sourceTemplate.conversationStarters || []);
            setAiRole(sourceTemplate.aiRole || 'UX Researcher');
            // Keep version and status as defaults for new template
            setVersion('1.0.0');
            setStatus('DRAFT');
          }
        })
        .catch(err => {
          console.error('Failed to fetch source template:', err);
          toast({
            title: 'Error',
            description: 'Failed to load template for duplication.',
            variant: 'destructive',
          });
        });
    }
  }, [isNew, duplicateFromId, token, toast]);

  useEffect(() => {
    if (!isNew && template) {
      const iconVal = template.icon || '📄';
      const nameVal = template.name;
      const descVal = template.description || '';
      const typeVal = template.type;
      const catVal = template.category;
      const instrVal = template.instructions || '';
      const outputVal = template.outputFormatSpec || '';
      const startersVal = template.conversationStarters || [];
      const aiRoleVal = (template as any).aiRole || 'UX Researcher';
      
      setIcon(iconVal);
      setName(nameVal);
      setDescription(descVal);
      setType(typeVal);
      setCategory(catVal);
      setInstructions(instrVal);
      setOutputFormatSpec(outputVal);
      setVersion(template.activeVersion);
      setStatus(template.status);
      setConversationStarters(startersVal);
      setAiRole(aiRoleVal);
      
      // Store original values for change detection
      setOriginalValues({
        icon: iconVal,
        name: nameVal,
        description: descVal,
        type: typeVal,
        category: catVal,
        instructions: instrVal,
        outputFormatSpec: outputVal,
        aiRole: aiRoleVal,
        conversationStarters: startersVal,
      });
    } else if (isNew && !duplicateFromId) {
      // Reset form for new template (not duplicating)
      setIcon('📄');
      setName('');
      setDescription('');
      setType('PERSONA');
      setCategory('discovery');
      setInstructions('');
      setOutputFormatSpec('');
      setVersion('1.0.0');
      setStatus('DRAFT');
      setConversationStarters([]);
      setOriginalValues(null); // No original for new templates
    }
  }, [template, isNew, duplicateFromId]);

  const handleSave = async () => {
    // Mark form as submitted to show validation errors
    setFormSubmitted(true);
    
    if (!isFormValid) {
      toast({
        title: 'Validation Error',
        description: validationErrors.join(', '),
        variant: 'destructive',
      });
      return;
    }

    try {
      const templateData = { 
        name, 
        description: description || undefined,
        icon,
        type: type as TemplateType, 
        category, 
        instructions,
        outputFormatSpec: outputFormatSpec || undefined,
        conversationStarters: conversationStarters.filter(s => s.trim()),
        aiRole: aiRole || undefined,
      };
      
      if (isNew) {
        const result = await createTemplate.mutateAsync(templateData);
        toast({ title: 'Template created', description: `"${name}" has been created as Draft.` });
        // Redirect to templates list with highlight for the new template (preserving list state)
        navigate(getBackUrl(result.id));
      } else {
        const result = await updateTemplate.mutateAsync({ templateId, ...templateData });
        toast({ 
          title: 'Template updated', 
          description: `"${name}" saved as v${result.activeVersion}. New version created in history.` 
        });
        // Redirect to templates list with highlight for the updated template (preserving list state)
        navigate(getBackUrl(templateId));
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save template.',
        variant: 'destructive',
      });
    }
  };

  const handleGeneratePreview = async () => {
    // Validate required fields
    if (!name.trim() || !instructions.trim()) {
      toast({
        title: 'Missing required fields',
        description: 'Please fill in the template name and instructions to generate a preview.',
        variant: 'destructive',
      });
      return;
    }

    setIsPreviewLoading(true);
    
    try {
      // Build the request payload with form data
      const payload = {
        prompt: testPrompt.trim() || undefined,
        model: selectedModel,
        name: name.trim(),
        instructions: instructions.trim(),
        outputFormatSpec: outputFormatSpec.trim() || undefined,
        type: TEMPLATE_TYPE_LABELS[type as keyof typeof TEMPLATE_TYPE_LABELS] || type,
        category: TEMPLATE_CATEGORY_LABELS[category as keyof typeof TEMPLATE_CATEGORY_LABELS] || category,
        aiRole: aiRole,
        description: description.trim() || undefined,
      };

      // Choose the appropriate API endpoint
      const apiUrl = (isNew || !templateId || templateId === 'new') 
        ? '/api/templates/preview'
        : `/api/templates/${templateId}/preview`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Failed to generate preview');
      }
      
      const data = await response.json();
      
      // Format the output with attached files info if any
      let output = data.data.output;
      if (attachedFiles.length > 0) {
        output += `\n\n📎 **Attached files:** ${attachedFiles.map(f => f.name).join(', ')}`;
      }
      
      setPreviewOutput(output);
      
      // Auto-switch to friendly view if JSON is detected
      try {
        const testParsed = JSON.parse(output.trim());
        if (testParsed && typeof testParsed === 'object') {
          // Always switch to friendly when JSON is generated
          setPreviewView('friendly');
        }
      } catch {
        // Not JSON, stay on current view (but default is friendly)
      }
      setTestPrompt(''); // Clear the prompt after generating
    } catch (error) {
      toast({
        title: 'Preview Error',
        description: error instanceof Error ? error.message : 'Failed to generate preview.',
        variant: 'destructive',
      });
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const addRelatedTemplate = (template: {id: string; name: string; category: string}) => {
    setRelatedTemplates([...relatedTemplates, template]);
    setSearchQuery('');
    setShowSearchResults(false);
  };
  
  const removeRelatedTemplate = (templateId: string) => {
    setRelatedTemplates(relatedTemplates.filter(t => t.id !== templateId));
  };

  if (!isNew && isTemplateLoading) {
    return (
      <div className="flex flex-col h-screen bg-white">
        <div className="border-b border-gray-200 px-6 py-4">
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="flex-1 flex">
          <div className="w-1/2 p-8 space-y-6 border-r border-gray-200">
            <Skeleton className="h-24 w-24 rounded-full mx-auto" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
          <div className="w-1/2 bg-gray-50 p-8">
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </div>
    );
  }

  // Show 404 for non-existent template or after loading completes with no data
  if (!isNew && (isTemplateError || (!isTemplateLoading && !template))) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 flex items-center justify-center p-6">
        {/* Decorative background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-100 rounded-full opacity-50 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-100 rounded-full opacity-50 blur-3xl" />
        </div>

        <div className="relative z-10 text-center max-w-lg">
          {/* Icon */}
          <div className="mb-8 flex justify-center">
            <div className="relative">
              <div className="w-32 h-32 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center shadow-2xl shadow-indigo-500/25 transform rotate-3 hover:rotate-0 transition-transform duration-300">
                <FileQuestion className="w-16 h-16 text-white" />
              </div>
              <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-orange-400 rounded-xl flex items-center justify-center shadow-lg transform -rotate-12">
                <Search className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* Error Code */}
          <h1 className="text-6xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-4">
            404
          </h1>

          {/* Title */}
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">
            Template Not Found
          </h2>

          {/* Description */}
          <p className="text-gray-600 mb-8 leading-relaxed">
            The template you're looking for doesn't exist or has been removed.
            Please check the URL or go back to the templates list.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/admin/templates"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white font-medium rounded-xl hover:from-indigo-700 hover:to-indigo-800 transition-all shadow-lg shadow-indigo-500/25"
            >
              <Home className="w-4 h-4" />
              Back to Templates
            </Link>
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-gray-700 font-medium rounded-xl border border-gray-200 hover:bg-gray-50 transition-all shadow-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Top Header - Full width white background */}
      <div className="border-b border-gray-200 bg-white shrink-0">
        <div className="container flex items-center justify-between px-8 py-5">
          <div className="flex items-center gap-5">
            <button
              onClick={() => navigate(getBackUrl())}
              className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
              title="Back to Templates"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="border-l border-gray-200 pl-5 flex items-center gap-4">
              <TemplateIcon 
                icon={icon} 
                category={category}
                size="md"
              />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {name || 'New Template'}
                </h1>
                <div className="flex items-center gap-3 mt-1">
                  <StatusBadge status={status} />
                  {!isNew && (
                    <span className="text-sm text-gray-500">v{version}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(getBackUrl())}
              className="px-4 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!isFormValid || !hasChanges || createTemplate.isPending || updateTemplate.isPending}
              className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
              title={!isFormValid ? validationErrors.join(', ') : !hasChanges ? 'No changes to save' : undefined}
            >
              {(createTemplate.isPending || updateTemplate.isPending) ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {isNew ? 'Create Template' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - Full width with split backgrounds */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - White background, full height */}
        <div className="w-1/2 bg-white overflow-y-auto border-r border-gray-200">
          <div className="max-w-[calc(950px-3rem)] ml-auto px-12 py-8 space-y-8">
          {/* Icon Selector */}
          <div className="flex justify-center">
            <div className="relative">
              {/* Icon button using TemplateIcon style */}
              <button 
                onClick={() => setShowIconPicker(!showIconPicker)}
                className="relative group"
              >
                {/* Large icon with category-based gradient */}
                <div className="transform hover:scale-105 transition-transform duration-200">
                  <TemplateIcon 
                    icon={icon} 
                    category={category}
                    size="lg"
                    className="w-24 h-24 text-4xl !rounded-2xl border-2 shadow-sm hover:shadow-md transition-shadow"
                  />
                </div>
              </button>
              
              {/* Icon picker modal */}
              {showIconPicker && (
                <>
                  {/* Backdrop to close picker */}
                  <div 
                    className="fixed inset-0 z-40 bg-black/20" 
                    onClick={() => setShowIconPicker(false)}
                  />
                  {/* Picker panel */}
                  <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 bg-white border border-gray-200 rounded-2xl shadow-2xl z-50 w-[480px] overflow-hidden">
                    {/* Header */}
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <TemplateIcon icon={icon} category={category} size="sm" />
                        <div>
                          <p className="font-medium text-gray-900">Template Icon</p>
                          <p className="text-xs text-gray-500">Choose an icon for your template</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => setShowIconPicker(false)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {/* Category tabs */}
                    <div className="px-4 pt-3 pb-2 border-b border-gray-100 overflow-x-auto">
                      <div className="flex gap-2 min-w-max">
                        {Object.keys(TEMPLATE_ICON_CATEGORIES).map((cat) => (
                          <button
                            key={cat}
                            onClick={() => setIconCategory(cat)}
                            className={cn(
                              'px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors',
                              iconCategory === cat
                                ? 'bg-indigo-600 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            )}
                          >
                            {cat}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Emoji grid */}
                    <div className="px-4 py-4 max-h-[280px] overflow-y-auto">
                      <div className="grid grid-cols-8 gap-2">
                        {TEMPLATE_ICON_CATEGORIES[iconCategory as keyof typeof TEMPLATE_ICON_CATEGORIES]?.map((emoji, index) => (
                          <button 
                            key={`${emoji}-${index}`}
                            onClick={() => { setIcon(emoji); setShowIconPicker(false); }}
                            className={cn(
                              "w-11 h-11 text-xl rounded-xl transition-all duration-150 flex items-center justify-center",
                              icon === emoji 
                                ? "bg-indigo-100 border-2 border-indigo-400 scale-105" 
                                : "hover:bg-gray-100 border-2 border-transparent hover:border-gray-200 hover:scale-110"
                            )}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Name */}
          <div>
            <RequiredLabel>Name</RequiredLabel>
              <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setTouched(prev => ({ ...prev, name: true }))}
              placeholder="Name your template"
              className={cn(
                "w-full px-4 py-3 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500",
                !name.trim() && (touched.name || formSubmitted) && "border-red-300"
              )}
            />
            {!name.trim() && (touched.name || formSubmitted) && (
              <p className="text-red-500 text-sm mt-1">Name is required</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-gray-900 font-medium mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a short description about what this template does"
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 resize-none"
            />
          </div>

          {/* Instructions */}
          <div>
            <RequiredLabel>Instructions</RequiredLabel>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              onBlur={() => setTouched(prev => ({ ...prev, instructions: true }))}
              placeholder="What does this template do? How should it behave? What should it avoid doing?"
              rows={10}
              className={cn(
                "w-full px-4 py-3 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 resize-none",
                !instructions.trim() && (touched.instructions || formSubmitted) && "border-red-300"
              )}
            />
            <p className="text-gray-500 text-sm mt-2">
              These instructions define how the AI will generate content using this template.
            </p>
          </div>

          {/* Output Format Specification */}
          <div>
            <label className="block text-gray-900 font-medium mb-2">
              Output Format Specification
            </label>
            <textarea
              value={outputFormatSpec}
              onChange={(e) => setOutputFormatSpec(e.target.value)}
              placeholder="Define the expected output format (JSON schema, Markdown structure, etc.)"
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 resize-none font-mono text-sm"
            />
            <p className="text-gray-500 text-sm mt-2">
              Specify the structure and format for AI-generated output.
            </p>
          </div>

          {/* Conversation Starters */}
          <div>
            <label className="block text-gray-900 font-medium mb-2">
              Conversation Starters
            </label>
            <div className="space-y-2">
              {conversationStarters.map((starter, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={starter}
                    onChange={(e) => {
                      const newStarters = [...conversationStarters];
                      newStarters[index] = e.target.value;
                      setConversationStarters(newStarters);
                    }}
                    placeholder="Add a conversation starter"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                  <button
                    onClick={() => setConversationStarters(conversationStarters.filter((_, i) => i !== index))}
                    className="p-3 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setConversationStarters([...conversationStarters, ''])}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors border border-indigo-200 hover:border-indigo-300"
              >
                <Plus className="w-4 h-4" />
                Add conversation starter
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 pt-8">
            <h3 className="text-gray-900 font-medium mb-6">Configuration</h3>
          </div>

          {/* Category */}
          <div>
            <RequiredLabel>Category</RequiredLabel>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-full h-12 rounded-lg">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="project">Project</SelectItem>
                <SelectItem value="discovery">Discovery</SelectItem>
                <SelectItem value="definition">Definition</SelectItem>
                <SelectItem value="design">Design</SelectItem>
                <SelectItem value="delivery">Delivery</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Type */}
          <div>
            <RequiredLabel>Template Type</RequiredLabel>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="w-full h-12 rounded-lg">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(TEMPLATE_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* AI Agent Role */}
          <div>
            <label className="block text-gray-700 mb-2">AI Agent Role</label>
            <Select value={aiRole} onValueChange={setAiRole}>
              <SelectTrigger className="w-full h-12 rounded-lg">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UX Researcher">UX Researcher</SelectItem>
                <SelectItem value="Product Owner">Product Owner</SelectItem>
                <SelectItem value="Product Strategist">Product Strategist</SelectItem>
                <SelectItem value="Business Analyst">Business Analyst</SelectItem>
                <SelectItem value="Market Analyst">Market Analyst</SelectItem>
                <SelectItem value="Innovation Strategist">Innovation Strategist</SelectItem>
                <SelectItem value="Technical Architect">Technical Architect</SelectItem>
                <SelectItem value="Service Designer">Service Designer</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Related Templates */}
          <div>
            <label className="block text-gray-700 mb-2">Related Templates</label>
            <p className="text-gray-500 text-sm mb-3">
              Link this template to related templates for cross-referencing
            </p>
            
            {/* Selected templates as chips */}
            {relatedTemplates.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {relatedTemplates.map((tpl) => (
                  <div
                    key={tpl.id}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-full"
                  >
                    <span className="text-sm">{tpl.name}</span>
                    <span className="text-xs text-indigo-500">({tpl.category})</span>
                    <button
                      onClick={() => removeRelatedTemplate(tpl.id)}
                      className="hover:bg-indigo-100 rounded-full p-0.5 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Search field */}
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowSearchResults(true);
                  }}
                  onFocus={() => setShowSearchResults(true)}
                  placeholder="Search for templates to link..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                />
              </div>
              
              {/* Search results dropdown - positioned above the input */}
              {showSearchResults && searchQuery && filteredTemplates.length > 0 && (
                <div className="absolute z-10 w-full bottom-full mb-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                  {filteredTemplates.map((tpl) => (
                    <button
                      key={tpl.id}
                      onClick={() => addRelatedTemplate(tpl)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between border-b border-gray-100 last:border-b-0"
                    >
                      <div>
                        <div className="text-gray-900">{tpl.name}</div>
                      </div>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {tpl.category}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              
              {showSearchResults && searchQuery && filteredTemplates.length === 0 && (
                <div className="absolute z-10 w-full bottom-full mb-1 bg-white border border-gray-200 rounded-xl shadow-xl p-4 text-center text-gray-500">
                  No templates found
                </div>
              )}
            </div>
          </div>

          {/* Version (read-only for existing) */}
          {!isNew && (
            <div>
              <label className="block text-gray-700 mb-2">Current Version</label>
              <div className="flex items-center gap-3">
                <span className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-mono">
                  v{version}
                </span>
              </div>
            </div>
          )}
          </div>
        </div>

        {/* Right Panel - Preview (Chat-like interface matching Figma) */}
        <div className="w-1/2 bg-slate-100 flex flex-col overflow-hidden">
          {/* Preview Header - Full width white background */}
          <div className="bg-white border-b border-gray-200 shrink-0">
            <div className="max-w-[calc(950px-3rem)] mr-auto px-6 py-4 flex items-center justify-between gap-4">
              <h3 className="text-gray-900 font-medium">Preview</h3>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <div className="flex items-center rounded-lg border border-gray-200 bg-gray-50 p-1">
                  <button
                    type="button"
                    onClick={() => setPreviewView('friendly')}
                    className={cn(
                      'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                      effectivePreviewView === 'friendly'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    )}
                  >
                    Friendly
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewView('raw')}
                    className={cn(
                      'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                      effectivePreviewView === 'raw'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    )}
                  >
                    Raw
                  </button>
                </div>
                <button
                  type="button"
                  onClick={handleCopyMarkdown}
                  disabled={!previewOutput.trim()}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
                  title="Copy as Markdown"
                >
                  {copyState === 'markdown' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  Copy as Markdown
                </button>
                <button
                  type="button"
                  onClick={handleCopyRaw}
                  disabled={!previewOutput.trim()}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
                  title="Copy raw output"
                >
                  {copyState === 'raw' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  Copy raw
                </button>
                <Select value={selectedModel} onValueChange={setSelectedModel}>
                  <SelectTrigger className="h-9 w-[160px] rounded-lg">
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent>
                    {MODEL_OPTIONS.map(model => (
                      <SelectItem key={model.value} value={model.value}>
                        Model {model.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          {/* Chat Preview Area */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="max-w-[calc(950px-3rem)] mr-auto h-full flex flex-col p-6">
              {/* Chat Messages Area */}
              <div className="flex-1 flex flex-col justify-end">
                {previewOutput ? (
                  /* Generated Preview Output */
                  <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-200 mb-4">
                    {effectivePreviewView === 'friendly' ? (
                      formattedPreview.isJson ? (
                        renderFriendlySummary(formattedPreview.parsed)
                      ) : (
                        /* Render markdown content in Friendly view with colored sections */
                        <FriendlyMarkdownRenderer content={formattedPreview.raw} />
                      )
                    ) : (
                      /* Raw view - show raw text */
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono m-0">
                        {formattedPreview.isJson ? formattedPreview.pretty : formattedPreview.raw}
                      </pre>
                    )}
                  </div>
                ) : (
                  /* AI Greeting Message */
                  <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-200 mb-4">
                    <p className="text-gray-700 leading-relaxed">
                      Hi! I'm a <strong className="text-gray-900">{aiRole}</strong> AI assistant
                      {name ? (
                        <>. I can help you create <strong className="text-gray-900">{TEMPLATE_TYPE_LABELS[type as TemplateType] || type}</strong> for your project.</>
                      ) : (
                        <>. Start by defining your template.</>
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer Controls */}
          <div className="bg-slate-100 border-t border-gray-200 shrink-0">
            <div className="max-w-[calc(950px-3rem)] mr-auto px-6 py-4">
              {/* Attached Files Preview */}
              {attachedFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {attachedFiles.map((file, i) => (
                    <div 
                      key={i}
                      className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm"
                    >
                      <Paperclip className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-gray-700 max-w-[150px] truncate">{file.name}</span>
                      <button
                        onClick={() => setAttachedFiles(files => files.filter((_, idx) => idx !== i))}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Bottom Action Buttons */}
              <div className="flex items-center gap-3">
                {/* Attach Button */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files) {
                      setAttachedFiles(prev => [...prev, ...Array.from(e.target.files!)]);
                    }
                  }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-12 h-12 flex items-center justify-center bg-white border border-gray-200 rounded-2xl hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
                  title="Attach files"
                >
                  <Plus className="w-5 h-5 text-gray-400" />
                </button>
                
                {/* Prompt Input */}
                <div className="flex-1 h-12 flex items-center bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                  <input
                    type="text"
                    value={testPrompt}
                    onChange={(e) => setTestPrompt(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && name.trim() && instructions.trim()) {
                        handleGeneratePreview();
                      }
                    }}
                    placeholder={name.trim() && instructions.trim() 
                      ? 'Type a message to test...'
                      : 'Start by defining your AI template.'
                    }
                    disabled={!name.trim() || !instructions.trim()}
                    className="flex-1 h-full px-4 bg-transparent border-none outline-none text-gray-700 placeholder-gray-400 disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div>

                {/* Submit Button */}
                <button
                  onClick={handleGeneratePreview}
                  disabled={isPreviewLoading || !name.trim() || !instructions.trim()}
                  className="w-12 h-12 flex items-center justify-center bg-indigo-600 text-white rounded-2xl hover:bg-indigo-700 transition-all disabled:bg-gray-300 disabled:cursor-not-allowed shadow-sm"
                  title="Generate Preview"
                >
                  {isPreviewLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
              
              {/* Conversation Starters - Show if defined */}
              {conversationStarters.filter(s => s.trim()).length > 0 && (
                <TooltipProvider delayDuration={300}>
                  <div className="flex gap-2 mt-4 overflow-x-auto pb-2 scrollbar-thin">
                    {conversationStarters.filter(s => s.trim()).map((starter, i) => (
                      <Tooltip key={i}>
                        <TooltipTrigger asChild>
                          <button 
                            onClick={() => setTestPrompt(starter)}
                            className="flex-shrink-0 px-4 py-2 bg-white border border-gray-200 rounded-full text-sm text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm whitespace-nowrap max-w-[250px] truncate"
                          >
                            {starter.length > 30 ? starter.slice(0, 30) + '...' : starter}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent 
                          side="top" 
                          className="max-w-[320px] text-sm bg-slate-900 text-white border-slate-800 shadow-xl px-3 py-2 rounded-lg"
                        >
                          <p className="whitespace-normal break-words">{starter}</p>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                </TooltipProvider>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
